import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient, useMutation, keepPreviousData } from '@tanstack/react-query';
// import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { MaskedInput } from '@/components/ui/MaskedInput';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import { NotificationService } from '@/services/notificationService';
import { useAuthStore } from '@/stores/authStore';
import { useKanbanStore } from '@/stores/kanbanStore';
import { useNotificationModal } from '@/contexts/NotificationModalContext';
import { 
  RefreshCw, 
  User, 
  Building2, 
  DollarSign, 
  AlertTriangle, 
  AlertCircle,
  MessageSquare, 
  Phone, 
  Mail, 
  MapPin,
  CheckCircle,
  FileText,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Calendar,
  Move,
  Users,
  Upload,
  X,
  Eye,
  Mic,
  MicOff,
  Volume2,
  Copy,
  ExternalLink,
  Send,
  TrendingUp,
  Target,
  Clock,
  Trophy,
  MessageCircle,
  Check,
  CheckCheck,
  Image as ImageIcon,
  Paperclip,
  Search,
  Bell,
  BellOff,
  Smile,
  Reply,
  Loader2,
  LayoutGrid,
  List
} from 'lucide-react';
import { LoadingState, safeArray, safeObject } from '@/components/safeRender';
import { AuthErrorState } from '@/components/AuthErrorState';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  effectiveTituloStatus,
  getFileUrl,
  parseCivilDateString,
} from '@/lib/utils';


import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import { Select } from '@/components/ui/Select';
import { useAccessibleKanbans } from '@/hooks/useAccessibleKanbans';
import { SortableCard } from '@/components/Kanban/SortableCard';
import { DroppableColumn } from '@/components/Kanban/DroppableColumn';
import { KanbanCardsList } from '@/components/Kanban/KanbanCardsList';
import {
  ordenarKanbanCards,
  readKanbanViewMode,
  writeKanbanViewMode,
  type KanbanViewMode,
} from '@/components/Kanban/kanbanCardUtils';
import { PaymentHistoryService } from '@/services/paymentHistoryService';
import { StatusPagamento, KanbanCard } from '@/types';
import { AudioPlayer } from '../components/AudioPlayer';
import { TituloTransferModal } from '@/components/TituloTransferModal';
import { WhatsAppBulkModal } from '@/components/Kanban/WhatsAppBulkModal';
import { KanbanCardModalLazy } from '@/components/Kanban/KanbanCardModal';

// Declarações de tipos para Web Speech API
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

/** Fixo na queryKey para descartar cache de respostas antigas (ex.: API com take 10k no kanban). */
const KANBAN_SIMPLE_TITULOS_QUERY_EPOCH = 'operador-carteira-v1';

export const KanbanPageSimple: React.FC = () => {
  const { user } = useAuthStore();
  const { selectedKanbanId } = useKanbanStore();
  const queryClient = useQueryClient();
  const { data: accessibleKanbans = [] } = useAccessibleKanbans();

  const selectedKanban = useMemo(
    () => accessibleKanbans.find((k) => k.id === selectedKanbanId) ?? null,
    [accessibleKanbans, selectedKanbanId]
  );
  const { isCardModalOpen, selectedClienteId, closeCardModal } = useNotificationModal();

  // API já aplica escopo/carteira por papel; não re-filtrar no cliente (apagava cards legítimos).
  const filterCardsByUserRole = (cards: any[]) => {
    if (!user) return [];
    return cards;
  };

  // Estados para o modal
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [isTransferKanbanModalOpen, setIsTransferKanbanModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'feedback' | 'titulos' | 'mudar-etapa' | 'transferir-usuario' | 'transferir-kanban' | 'whatsapp' | 'pagamentos' | 'timeline'>('feedback');
  const [chatMessage, setChatMessage] = useState('');
  const chatMessagesEndRef = React.useRef<HTMLDivElement>(null);
  
  // Estados para melhorias do chat WhatsApp
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [replyingTo, setReplyingTo] = useState<any>(null);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);

  // Query para buscar mensagens do chat WhatsApp
  const { data: chatMessages = [], refetch: refetchChatMessages } = useQuery({
    queryKey: ['whatsapp-chat-messages', selectedCard?.cliente?.id],
    queryFn: async () => {
      if (!selectedCard?.cliente?.id) return [];
      const response = await api.get(`/whatsapp-integration/chat/messages/${selectedCard.cliente.id}`);
      return response.data.data.messages || [];
    },
    enabled: !!selectedCard?.cliente?.id && activeTab === 'whatsapp',
    refetchInterval: 5000, // Atualizar a cada 5 segundos
  });

  // Mutation para enviar mensagem (com suporte a mídia)
  const sendChatMessageMutation = useMutation({
    mutationFn: async ({ content, media }: { content: string; media?: File }) => {
      if (!selectedCard?.cliente?.id) throw new Error('Cliente não selecionado');
      
      const formData = new FormData();
      formData.append('clienteFornecedorId', selectedCard.cliente.id);
      if (content) {
        formData.append('content', content);
      }
      if (media) {
        formData.append('media', media);
      }

      const response = await api.post('/whatsapp-integration/chat/send', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data;
    },
    onSuccess: () => {
      setChatMessage('');
      setSelectedMedia(null);
      setMediaPreview(null);
      refetchChatMessages();
      toast.success('Mensagem enviada com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao enviar mensagem');
    },
  });

  const isSendingChatMessage = sendChatMessageMutation.isPending;

  const handleSendChatMessage = () => {
    if ((!chatMessage.trim() && !selectedMedia) || !selectedCard?.cliente?.id) return;
    
    sendChatMessageMutation.mutate({
      content: chatMessage.trim(),
      media: selectedMedia || undefined,
    });
  };

  // Função para formatar links e números (escape HTML + allowlist http/https)
  const formatMessageContent = (content: string) => {
    const escapeHtml = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const escaped = escapeHtml(content || '');
    const urlRegex = /(https?:\/\/[^\s&lt;]+)/g;
    const phoneRegex = /(\+?55\s?)?(\(?\d{2}\)?\s?)?(\d{4,5}[-.\s]?\d{4})/g;

    let formatted = escaped.replace(urlRegex, (url) => {
      try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          return url;
        }
        const safeHref = escapeHtml(parsed.toString());
        return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" class="text-blue-400 underline hover:text-blue-300">${url}</a>`;
      } catch {
        return url;
      }
    });

    formatted = formatted.replace(phoneRegex, (phone) => {
      const cleanPhone = phone.replace(/\D/g, '');
      return `<a href="tel:${cleanPhone}" class="text-blue-400 underline hover:text-blue-300">${phone}</a>`;
    });

    return formatted;
  };

  // Função para obter ícone de status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <Check className="w-3 h-3" />;
      case 'delivered':
        return <CheckCheck className="w-3 h-3" />;
      case 'read':
        return <CheckCheck className="w-3 h-3 text-blue-300" />;
      case 'failed':
        return <X className="w-3 h-3 text-red-300" />;
      default:
        return <Loader2 className="w-3 h-3 animate-spin" />;
    }
  };

  // Função para obter texto de status
  const getStatusText = (status: string) => {
    switch (status) {
      case 'sent':
        return 'Enviado';
      case 'delivered':
        return 'Entregue';
      case 'read':
        return 'Lido';
      case 'failed':
        return 'Falhou';
      default:
        return 'Enviando...';
    }
  };

  // Detectar digitação
  const handleTyping = () => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    setIsTyping(true);
    
    const timeout = setTimeout(() => {
      setIsTyping(false);
    }, 3000);
    
    setTypingTimeout(timeout);
  };

  // Carregar templates para quick replies
  const { data: whatsappTemplates = [] } = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: async () => {
      const response = await api.get('/whatsapp-integration/templates');
      return response.data.data.templates || [];
    },
    enabled: activeTab === 'whatsapp',
  });

  // Filtrar mensagens por busca
  const filteredMessages = React.useMemo(() => {
    if (!searchQuery.trim()) return chatMessages;
    const query = searchQuery.toLowerCase();
    return chatMessages.filter((msg: any) => 
      msg.content?.toLowerCase().includes(query)
    );
  }, [chatMessages, searchQuery]);

  // Agrupar mensagens por data (mantido para uso futuro)
  // const groupedMessages = React.useMemo(() => {
  //   const groups: { [key: string]: any[] } = {};
  //   filteredMessages.forEach((msg: any) => {
  //     const date = new Date(msg.timestamp);
  //     const dateKey = date.toLocaleDateString('pt-BR', { 
  //       day: '2-digit', 
  //       month: '2-digit', 
  //       year: 'numeric' 
  //     });
  //     if (!groups[dateKey]) {
  //       groups[dateKey] = [];
  //     }
  //     groups[dateKey].push(msg);
  //   });
  //   return groups;
  // }, [filteredMessages]);

  // Função para verificar se é o mesmo dia
  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  // Contar mensagens não lidas
  useEffect(() => {
    if (activeTab === 'whatsapp') {
      setUnreadCount(0);
    } else {
      const unread = chatMessages.filter((msg: any) => 
        msg.direction === 'inbound' && 
        (!msg.readAt || new Date(msg.readAt) < new Date(msg.timestamp))
      ).length;
      setUnreadCount(unread);
    }
  }, [chatMessages, activeTab]);

  // Notificação sonora para novas mensagens
  useEffect(() => {
    if (chatMessages.length > 0 && soundEnabled) {
      const lastMessage = chatMessages[chatMessages.length - 1];
      if (lastMessage.id !== lastMessageId && lastMessage.direction === 'inbound') {
        // Tocar som de notificação
        const audio = new Audio('/notification.mp3');
        audio.volume = 0.5;
        audio.play().catch(() => {
          // Ignorar erro se o navegador bloquear autoplay
        });
        setLastMessageId(lastMessage.id);
      }
    }
  }, [chatMessages, soundEnabled, lastMessageId]);

  // Scroll para última mensagem quando novas mensagens chegarem
  useEffect(() => {
    if (activeTab === 'whatsapp' && chatMessagesEndRef.current) {
      chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, activeTab]);

  // Limpar timeout ao desmontar
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);
  const [formData, setFormData] = useState({
    tipo: 'CONTATO',
    descricao: '',
    proximoContato: '',
    valorNegociado: '',
    tipoNegociacao: '',
    tituloId: '',
    titulosSelecionados: [] as string[],
    acaoGestor: false,
  });

  // Estados para upload de arquivos
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  
  // Estados para seleção de cards
  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  
  // Estados para transcrição de áudio
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [recognition, setRecognition] = useState<any>(null);
  
  // Estados para captura de áudio
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [isAudioRecording, setIsAudioRecording] = useState(false);
  
  // Estados para drag and drop
  const [activeCard, setActiveCard] = useState<any>(null);
  
  // Estado para controlar visualização (kanban vs lista)
  const [viewMode, setViewMode] = useState<KanbanViewMode>(() => readKanbanViewMode());
  const [listMovingCardId, setListMovingCardId] = useState<string | null>(null);
  
  // Estados para ordenação
  const [sortBy, setSortBy] = useState<'diasAtraso' | 'valorVencido'>('diasAtraso');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const setViewModePersist = useCallback((mode: KanbanViewMode) => {
    setViewMode(mode);
    writeKanbanViewMode(mode);
  }, []);
  
  // Estados para filtros
  const [selectedOperador, setSelectedOperador] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [selectedGestorAction, setSelectedGestorAction] = useState<string>('');
  const [selectedAtrasoFilter, setSelectedAtrasoFilter] = useState<string>(''); // NOVO: Filtro de dias de atraso
  const [clienteSearchInput, setClienteSearchInput] = useState('');
  const [clienteSearch, setClienteSearch] = useState('');
  const [operadores, setOperadores] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingOperadores, setLoadingOperadores] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // Estados para mudança de etapa no modal
  const [selectedEtapaId, setSelectedEtapaId] = useState<string>('');
  const [isChangingEtapa, setIsChangingEtapa] = useState(false);
  
  // Estados para transferência de usuário no modal
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isTransferringUser, setIsTransferringUser] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  

  const [titulosActiveTab, setTitulosActiveTab] = useState<'vencidos' | 'parciais'>('vencidos');

  // Pagamento (timeline simples)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentFeedbackId, setPaymentFeedbackId] = useState('');
  const [paymentValorNeg, setPaymentValorNeg] = useState('');
  const [paymentValorPago, setPaymentValorPago] = useState('');
  const [paymentData, setPaymentData] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<StatusPagamento>(StatusPagamento.CONFIRMADO);
  const [paymentObs, setPaymentObs] = useState('');
  const [paymentFile, setPaymentFile] = useState<File | null>(null);
  // NOVO: seleção múltipla e estratégia
  const [paymentTitulosSelecionados, setPaymentTitulosSelecionados] = useState<string[]>([]);
  const [paymentDistribuicao, setPaymentDistribuicao] = useState<'MAIS_ANTIGOS' | 'PROPORCIONAL'>('MAIS_ANTIGOS');


  const [isStatsOpen, setIsStatsOpen] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setClienteSearch(clienteSearchInput.trim()), 350);
    return () => window.clearTimeout(t);
  }, [clienteSearchInput]);

  
  // Helpers de pagamento
  const parseCurrency = (s: string) => {
	if (!s) return 0;
	// Remove todos os caracteres não numéricos exceto vírgula e ponto
	const cleaned = s.replace(/[^\d,.-]/g, '');
	// Se tem vírgula e ponto, assume que vírgula é separador decimal
	if (cleaned.includes(',') && cleaned.includes('.')) {
		const normalized = cleaned.replace(/\./g, '').replace(',', '.');
		return parseFloat(normalized || '0');
	}
	// Se só tem vírgula, assume que é separador decimal
	if (cleaned.includes(',')) {
		const normalized = cleaned.replace(',', '.');
		return parseFloat(normalized || '0');
	}
	// Se só tem ponto ou nenhum separador
	return parseFloat(cleaned || '0');
};

  const getSelectedTitulos = (): any[] => {
	// Buscar feedback original para pegar os títulos marcados
	const feedbackOriginal = feedbacks.find((f: any) => f.id === paymentFeedbackId);
	const titulosDoFeedback = feedbackOriginal?.titulosNegociados || [];
	// Filtrar apenas os selecionados
	return titulosDoFeedback.filter((t: any) => paymentTitulosSelecionados.includes(t.id));
};

  const getSaldoSelecionado = (): number => {
	const titulos = getSelectedTitulos();
	const saldo = titulos.reduce((sum: number, t: any) => sum + Number(t.saldo || t.valor || 0), 0);
	console.log('🔍 Debug getSaldoSelecionado:', {
		titulosCount: titulos.length,
		titulos: titulos.map(t => ({ id: t.id, saldo: t.saldo, valor: t.valor })),
		saldoTotal: saldo
	});
	return saldo;
};

  const applyQuickAction = (action: 'igualSaldo' | 'menos5' | 'menos10' | 'arredondar' | 'zerarCentavos') => {
	const saldo = getSaldoSelecionado();
	let novo = saldo;
	switch (action) {
		case 'igualSaldo': novo = saldo; break;
		case 'menos5': novo = saldo * 0.95; break;
		case 'menos10': novo = saldo * 0.90; break;
		case 'arredondar': novo = Math.round(saldo); break;
		case 'zerarCentavos': novo = Math.floor(saldo); break;
	}
	// Formatar para a máscara
	const valorFormatado = novo > 0 ? String(novo.toFixed(2)).replace('.', ',') : '0,00';
	setPaymentValorNeg(valorFormatado);
};

// Handlers para foco dos campos
const handleValorNegFocus = () => {
	// Se o campo tem valor e não está vazio, permite edição sem limpar
	if (!paymentValorNeg || paymentValorNeg === '0,00' || paymentValorNeg === '0') {
		setPaymentValorNeg('');
	}
};

const handleValorNegBlur = () => {
	// Se o campo ficou vazio, volta ao valor padrão
	if (!paymentValorNeg || paymentValorNeg.trim() === '') {
		const saldo = getSaldoSelecionado();
		const valorFormatado = saldo > 0 ? String(saldo.toFixed(2)).replace('.', ',') : '0,00';
		setPaymentValorNeg(valorFormatado);
	}
};

const handleValorPagoFocus = () => {
	// Se o campo tem valor e não está vazio, permite edição sem limpar
	if (!paymentValorPago || paymentValorPago === '0,00' || paymentValorPago === '0') {
		setPaymentValorPago('');
	}
};

const handleValorPagoBlur = () => {
	// Se o campo ficou vazio, volta ao valor padrão
	if (!paymentValorPago || paymentValorPago.trim() === '') {
		setPaymentValorPago('0,00');
	}
};

  const distribuirValor = (valorTotal: number, titulos: any[]): Record<string, number> => {
    if (titulos.length === 0 || valorTotal <= 0) return {};
    let alloc: Record<string, number> = {};
    if (paymentDistribuicao === 'MAIS_ANTIGOS') {
      // Ordena por vencimento asc
      const ordenados = [...titulos].sort(
        (a, b) => parseCivilDateString(a.vencimento).getTime() - parseCivilDateString(b.vencimento).getTime()
      );
      let restante = valorTotal;
      for (const t of ordenados) {
        const saldo = Number(t.saldo || t.valor || 0);
        const pago = Math.min(restante, saldo);
        alloc[t.id] = Number(pago.toFixed(2));
        restante -= pago;
        if (restante <= 0) break;
      }
    } else {
      // PROPORCIONAL
      const totalSaldo = titulos.reduce((sum, t) => sum + Number(t.saldo || t.valor || 0), 0);
      if (totalSaldo <= 0) return {};
      for (const t of titulos) {
        const saldo = Number(t.saldo || t.valor || 0);
        const base = (saldo / totalSaldo) * valorTotal;
        alloc[t.id] = Number(base.toFixed(2));
      }
      // Ajuste de arredondamento no último
      const soma = Object.values(alloc).reduce((s, v) => s + v, 0);
      const diff = Number((valorTotal - soma).toFixed(2));
      if (Math.abs(diff) >= 0.01) {
        const lastId = titulos[titulos.length - 1].id;
        alloc[lastId] = Number((alloc[lastId] + diff).toFixed(2));
      }
    }
    return alloc;
  };

  const handleOpenPayment = (feedback: any) => {
    // Usar títulos do feedback original
    const titulosDoFeedback = Array.isArray(feedback?.titulosNegociados) ? feedback.titulosNegociados : [];
    const idsFromFeedback = titulosDoFeedback.map((t: any) => (typeof t === 'string' ? t : t.id));
    
    // Se não há títulos no feedback, usar títulos vencidos do card como fallback
    const allTitulos = Array.isArray(selectedCard?.titulos) ? selectedCard!.titulos : [];
    const vencidos = allTitulos.filter(
      (t: any) => effectiveTituloStatus(t.status, t.vencimento) === 'VENCIDO'
    );
    const tituloIdPref = (idsFromFeedback[0])
      || feedback.tituloId
      || (vencidos[0]?.id)
      || '';
    const defaultSelecionados = idsFromFeedback.length > 0 ? idsFromFeedback : (tituloIdPref ? [tituloIdPref] : []);

    	setPaymentFeedbackId(feedback.id);
	setPaymentTitulosSelecionados(defaultSelecionados);
	
	// valor negociado default = soma dos saldos/valores dos títulos do feedback selecionados
	const soma = titulosDoFeedback
		.filter((t: any) => defaultSelecionados.includes(t.id))
		.reduce((sum: number, t: any) => sum + Number(t.saldo || t.valor || 0), 0);
	
	// Formatar valores corretamente para a máscara
	const valorFormatado = soma > 0 ? String(soma.toFixed(2)).replace('.', ',') : '0,00';
	setPaymentValorNeg(valorFormatado);
	setPaymentValorPago('0,00');
	setPaymentData('');
	setPaymentDistribuicao('MAIS_ANTIGOS');
	setPaymentStatus(StatusPagamento.CONFIRMADO);
	setPaymentObs('');
	setPaymentFile(null);
	setPaymentModalOpen(true);
  };

  const handleSubmitPayment = async () => {
	if (!paymentFeedbackId || paymentTitulosSelecionados.length === 0) {
		setPaymentModalOpen(false);
		return;
	}
	try {
		// Upload do comprovante (opcional)
		let comprovanteId: string | undefined = undefined;
		if (paymentFile && selectedCard?.cliente?.id) {
			const form = new FormData();
			form.append('files', paymentFile);
			form.append('clienteFornecedorId', selectedCard.cliente.id);
			form.append('feedbackId', paymentFeedbackId);
			const res = await api.post('/upload/comprovantes', form, {
				headers: { 'Content-Type': 'multipart/form-data' },
			});
			const file = res.data?.data?.files?.[0];
			if (file?.id) comprovanteId = file.id as string;
		}

		const valorTotalNeg = parseCurrency(paymentValorNeg);
		const titulos = getSelectedTitulos();
		const alocacao = distribuirValor(valorTotalNeg, titulos);
		const valorPagoNum = paymentValorPago ? parseCurrency(paymentValorPago) : undefined;

		console.log('🔍 Debug handleSubmitPayment:', {
			paymentValorNeg,
			valorTotalNeg,
			titulosCount: titulos.length,
			titulos: titulos.map(t => ({ id: t.id, saldo: t.saldo, valor: t.valor })),
			alocacao,
			valorPagoNum
		});

		// Criar histórico por título
		for (const t of titulos) {
			const vNeg = alocacao[t.id] || 0;
			const vPago = valorPagoNum != null ? Number(((vNeg / Math.max(valorTotalNeg, 1)) * valorPagoNum).toFixed(2)) : undefined;
			await PaymentHistoryService.create({
				feedbackId: paymentFeedbackId,
				tituloId: t.id,
				valorNegociado: Number(vNeg.toFixed(2)),
				...(vPago != null ? { valorPago: vPago } : {}),
				...(paymentData ? { dataPagamento: paymentData } : {}),
				statusPagamento: paymentStatus,
				...(paymentObs ? { observacoes: paymentObs } : {}),
				...(comprovanteId ? { comprovanteId } : {}),
			});
		}

		// Criar feedback para histórico e acionar gestor
		if (selectedCard?.cliente?.id) {
			const diff = Number((valorTotalNeg - getSaldoSelecionado()).toFixed(2));
			const perc = getSaldoSelecionado() > 0 ? Number(((valorTotalNeg / getSaldoSelecionado()) * 100 - 100).toFixed(2)) : 0;
			const descricaoResumo = `Pagamento/Negociação registrada. Valor negociado: R$ ${formatCurrency(valorTotalNeg)}. Saldo selecionado: R$ ${formatCurrency(getSaldoSelecionado())}. Diferença: R$ ${formatCurrency(diff)} (${perc}%). Estratégia: ${paymentDistribuicao === 'MAIS_ANTIGOS' ? 'Mais antigos primeiro' : 'Proporcional'}.`;
			await api.post('/titulos/feedback', {
				clienteFornecedorId: selectedCard.cliente.id,
				tituloId: paymentTitulosSelecionados[0] || null,
				titulosNegociados: paymentTitulosSelecionados,
				tipo: 'NEGOCIACAO',
				descricao: `${descricaoResumo}${paymentObs ? ` Observações: ${paymentObs}` : ''}`,
				tipoNegociacao: 'PAGO',
				valorNegociado: String(valorTotalNeg),
				proximoContato: null,
				acaoGestor: true,
			});
		}

		toast.success('Pagamento registrado no histórico e enviado ao gestor');
		setPaymentModalOpen(false);

		// Atualizar dados
		await queryClient.invalidateQueries({ queryKey: ['kanban-simple'] });
		await queryClient.invalidateQueries({ queryKey: ['kanban'] });
		await queryClient.invalidateQueries({ queryKey: ['kanban-estatisticas'] });
		if (selectedCard?.cliente?.id) {
			await queryClient.invalidateQueries({ queryKey: ['feedbacks', selectedCard.cliente.id] });
		}
	} catch (e: any) {
		toast.error(e?.response?.data?.message || 'Erro ao registrar pagamento');
	}
};

  // Função para copiar URL para área de transferência
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('URL copiada para a área de transferência');
    } catch (error) {
      toast.error('Erro ao copiar URL');
    }
  };

  // Função para enviar URL por WhatsApp
  const sendToWhatsApp = (url: string, tituloNumero: string) => {
    const message = `Olá! Segue o link do boleto ${tituloNumero}: ${url}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Abrindo WhatsApp...');
  };

  // Função para enviar URL por e-mail
  const sendToEmail = async (url: string, tituloNumero: string) => {
    try {
      // Buscar email do cliente se disponível
      const clienteEmail = selectedCard?.cliente?.email;
      
      if (!clienteEmail) {
        // Se não há email do cliente, usar mailto como fallback
        const subject = `Boleto ${tituloNumero}`;
        const body = `Olá,\n\nSegue o link do boleto ${tituloNumero}:\n${url}\n\nAtenciosamente.`;
        const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.open(mailtoUrl, '_blank');
        toast.success('Abrindo cliente de e-mail...');
        
        // Registrar como feedback mesmo sem email do cliente
        await registerEmailFeedback(tituloNumero, url, 'Cliente de e-mail local');
        return;
      }

      // Enviar email via API
      await api.post('/email/boleto', {
        to: clienteEmail,
        tituloNumero,
        urlBoleto: url,
        companyId: selectedCard?.companyId,
      });

      toast.success('Email do boleto enviado com sucesso!');
      
      // Registrar como feedback após envio bem-sucedido
      await registerEmailFeedback(tituloNumero, url, clienteEmail);
      
    } catch (error: any) {
      console.error('Erro ao enviar email:', error);
      toast.error(error.response?.data?.message || 'Erro ao enviar email');
      
      // Fallback para mailto se a API falhar
      const subject = `Boleto ${tituloNumero}`;
      const body = `Olá,\n\nSegue o link do boleto ${tituloNumero}:\n${url}\n\nAtenciosamente.`;
      const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoUrl, '_blank');
      
      // Registrar como feedback mesmo com erro na API
      await registerEmailFeedback(tituloNumero, url, selectedCard?.cliente?.email || 'Cliente de e-mail local');
    }
  };

  // Função para registrar envio de email como feedback
  const registerEmailFeedback = async (tituloNumero: string, url: string, emailDestino: string) => {
    try {
      // Encontrar o título correspondente
      const titulo = selectedCard?.titulos?.find((t: any) => t.numero === tituloNumero);
      
      if (!titulo) {
        console.warn('Título não encontrado para registro de feedback');
        return;
      }

      // Registrar feedback
      await api.post('/titulos/feedback', {
        tituloId: titulo.id,
        clienteFornecedorId: selectedCard?.cliente?.id,
        tipo: 'EMAIL',
        descricao: `Email enviado para ${emailDestino} com link do boleto ${tituloNumero}:\n${url}`,
        proximoContato: undefined,
        valorNegociado: undefined,
        titulosNegociados: undefined,
        valorTotalNegociado: undefined,
        dataVencimentoNegociacao: undefined,
        statusNegociacao: undefined,
        observacoesPagamento: undefined,
      });

      // Recarregar feedbacks do modal
      if (selectedCard?.cliente?.id) {
        const response = await api.get(`/titulos/feedback/cliente/${selectedCard.cliente.id}`);
        const feedbacksData = response.data?.data?.feedbacks || response.data?.data || [];
        console.log('Feedbacks recarregados após envio de email:', feedbacksData);
        setFeedbacks(feedbacksData);
      }

      // Atualizar dados do Kanban
      await queryClient.invalidateQueries({ queryKey: ['kanban-simple'] });
      await queryClient.invalidateQueries({ queryKey: ['kanban'] });
      await queryClient.invalidateQueries({ queryKey: ['kanban-estatisticas'] });
      
      console.log('Feedback de email registrado com sucesso e modal atualizado');
    } catch (error: any) {
      console.error('Erro ao registrar feedback de email:', error);
      // Não mostrar toast de erro para não poluir a interface
    }
  };

  // Configuração dos sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const { 
    data: kanbanData, 
    isLoading,
    isFetching,
    error, 
    refetch 
  } = useQuery({
    queryKey: [
      'kanban-simple',
      KANBAN_SIMPLE_TITULOS_QUERY_EPOCH,
      selectedOperador,
      selectedCompany,
      selectedGestorAction,
      selectedAtrasoFilter,
      selectedKanbanId,
      clienteSearch,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedOperador) params.append('operadorId', selectedOperador);
      if (selectedCompany) params.append('companyId', selectedCompany);
      if (selectedGestorAction) params.append('gestorAction', selectedGestorAction);
      if (selectedAtrasoFilter) params.append('diasAtraso', selectedAtrasoFilter);
      if (selectedKanbanId) params.append('kanbanId', selectedKanbanId);
      if (clienteSearch) params.append('search', clienteSearch);

      const response = await api.get(`/titulos/kanban?${params.toString()}`);
      return response.data;
    },
    placeholderData: keepPreviousData,
    retry: 1,
    enabled: !!selectedKanbanId,
    refetchInterval: () => {
      if (typeof document !== 'undefined' && document.hidden) return false;
      if (isModalOpen) return false;
      return 60000;
    },
    refetchIntervalInBackground: false,
  });

  // Abrir modal quando notificação for clicada
  useEffect(() => {
    if (isCardModalOpen && selectedClienteId && kanbanData) {
      // Buscar o card em todas as etapas do kanban
      const etapas = safeArray((kanbanData as any)?.data?.etapas);
      const kanban = safeObject((kanbanData as any)?.data?.kanban);
      
      let foundCard = null;
      for (const etapa of etapas) {
        const cards = safeArray(kanban[(etapa as any).id]);
        const card = cards.find((c: any) => c.cliente?.id === selectedClienteId);
        if (card) {
          foundCard = card;
          break;
        }
      }
      
      if (foundCard) {
        setSelectedCard(foundCard);
        setIsModalOpen(true);
        closeCardModal(); // Fechar o contexto após abrir o modal
      }
    }
  }, [isCardModalOpen, selectedClienteId, kanbanData, closeCardModal]);

  // Carregar dados iniciais
  useEffect(() => {
    if (user?.role === 'ADMIN' || user?.role === 'GESTOR') {
      loadOperadores();
      loadCompanies();
    }
  }, [user?.role]);



  // Limpar transcrição quando modal for fechado
  useEffect(() => {
    if (!isModalOpen) {
      if (isRecording) {
        stopRecording();
      }
      setTranscript('');
      clearAudio();
    }
  }, [isModalOpen]);

  // Carregar WhatsApp quando modal abrir
  useEffect(() => {
    if (isModalOpen && selectedCard?.cliente) {
      
    }
  }, [isModalOpen, selectedCard]);



  // Buscar estatísticas do kanban
  const { 
    data: estatisticasData, 
    isLoading: isLoadingStats 
  } = useQuery({
    queryKey: ['kanban-estatisticas', selectedOperador, selectedCompany, selectedGestorAction, selectedAtrasoFilter, selectedKanbanId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedOperador) params.append('operadorId', selectedOperador);
      if (selectedCompany) params.append('companyId', selectedCompany);
      if (selectedGestorAction) params.append('gestorAction', selectedGestorAction);
      if (selectedAtrasoFilter) params.append('diasAtraso', selectedAtrasoFilter);
      if (selectedKanbanId) params.append('kanbanId', selectedKanbanId);
      
      const response = await api.get(`/titulos/estatisticas-kanban?${params.toString()}`);
      return response.data;
    },
    retry: 1,
    enabled: isStatsOpen && !!selectedKanbanId,
  });

  // Buscar performance individual do operador
  const { data: performanceData, isLoading: isLoadingPerformance } = useQuery({
    queryKey: ['operator-performance'],
    queryFn: async () => {
      const response = await api.get('/operator/performance');
      return response.data;
    },
    enabled: user?.role === 'OPERADOR' && isStatsOpen,
    refetchInterval: isStatsOpen ? 60000 : false,
  });

  console.log('KanbanPageSimple - Estado atual:', { kanbanData, isLoading, error });



  const handleRefresh = () => {
    console.log('KanbanPageSimple - Atualizando dados');
    refetch();
    queryClient.invalidateQueries({ queryKey: ['kanban-estatisticas'] });
    toast.success('Dados atualizados!');
  };

  // Função para validar consistência das contagens
  const validateCardCounts = () => {
    if (!kanbanData?.data?.kanban) return;

    const kanban = kanbanData.data.kanban;
    const etapas = kanbanData.data.etapas || [];
    
    // Calcular total de cards em todas as etapas
    const totalCards = etapas.reduce((total: number, etapa: any) => {
      const cards = safeArray(kanban[etapa.id]);
      return total + cards.length;
    }, 0);

    // Calcular total de clientes únicos
    const clientesUnicos = new Set();
    etapas.forEach((etapa: any) => {
      const cards = safeArray(kanban[etapa.id]);
      cards.forEach((card: any) => {
        if (card.cliente?.id) {
          clientesUnicos.add(card.cliente.id);
        }
      });
    });

    console.log('Validação de contagens:', {
      totalCards,
      totalClientesUnicos: clientesUnicos.size,
      estatisticasTotalClientes: estatisticasData?.data?.estatisticas?.totalClientes || 0,
      filtrosAtivos: {
        operador: selectedOperador || 'Todos',
        empresa: selectedCompany || 'Todas',
        atraso: selectedAtrasoFilter || 'Todos'
      }
    });

    // Verificar se as contagens são consistentes
    const estatisticasClientes = estatisticasData?.data?.estatisticas?.totalClientes || 0;
    if (estatisticasClientes !== clientesUnicos.size) {
      console.warn('Inconsistência detectada:', {
        estatisticas: estatisticasClientes,
        cards: clientesUnicos.size,
        diferenca: estatisticasClientes - clientesUnicos.size
      });
    }
  };

  // Validar contagens quando os dados mudarem
  useEffect(() => {
    if (kanbanData && estatisticasData) {
      validateCardCounts();
    }
  }, [kanbanData, estatisticasData, selectedOperador, selectedCompany, selectedAtrasoFilter]);

  // Funções para transcrição de áudio
  useEffect(() => {
    // Verificar se o navegador suporta Web Speech API
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      // Configurações para melhor performance
      recognitionInstance.continuous = false; // Mudança para false para permitir parar
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'pt-BR';
      recognitionInstance.maxAlternatives = 1;
      
      recognitionInstance.onstart = () => {
        console.log('Gravação iniciada');
        setIsRecording(true);
        setTranscript('');
        toast.success('🎤 Gravação iniciada! Fale agora...');
      };
      
      recognitionInstance.onresult = (event: any) => {
        console.log('Resultado recebido:', event.results);
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        const newTranscript = finalTranscript + interimTranscript;
        console.log('Transcrição atual:', newTranscript);
        setTranscript(newTranscript);
      };
      
      recognitionInstance.onerror = (event: any) => {
        console.error('Erro na transcrição:', event.error);
        setIsRecording(false);
        
        if (event.error === 'not-allowed') {
          toast.error('❌ Permissão de microfone negada. Permita o acesso ao microfone.');
        } else if (event.error === 'no-speech') {
          toast.error('🔇 Nenhum áudio detectado. Tente falar mais alto.');
        } else if (event.error === 'network') {
          toast.error('🌐 Erro de rede. Verifique sua conexão.');
        } else {
          toast.error(`❌ Erro na transcrição: ${event.error}`);
        }
      };
      
      recognitionInstance.onend = () => {
        console.log('Gravação finalizada');
        setIsRecording(false);
        if (transcript.trim()) {
          toast.success('✅ Transcrição concluída!');
        }
      };
      
      setRecognition(recognitionInstance);
    } else {
      console.error('Web Speech API não suportada');
      toast.error('❌ Seu navegador não suporta transcrição de áudio.');
    }
  }, []);

  const startRecording = async () => {
    if (recognition) {
      try {
        console.log('Iniciando gravação de transcrição e áudio...');
        
        // Iniciar transcrição
        recognition.start();
        
        // Iniciar gravação de áudio
        await startAudioRecording();
        
      } catch (error) {
        console.error('Erro ao iniciar gravação:', error);
        toast.error('❌ Erro ao iniciar gravação. Tente novamente.');
        setIsRecording(false);
        setIsAudioRecording(false);
      }
    } else {
      toast.error('❌ Transcrição de áudio não disponível.');
    }
  };

  const stopRecording = () => {
    if (recognition && isRecording) {
      try {
        console.log('Parando gravação...');
        recognition.stop();
      } catch (error) {
        console.error('Erro ao parar gravação:', error);
        setIsRecording(false);
      }
    }
    
    // Parar gravação de áudio
    if (isAudioRecording) {
      stopAudioRecording();
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const applyTranscript = () => {
    if (transcript.trim()) {
      setFormData(prev => ({
        ...prev,
        descricao: prev.descricao + (prev.descricao ? ' ' : '') + transcript
      }));
      setTranscript('');
      toast.success('Transcrição aplicada ao campo descrição!');
    }
  };

  const clearTranscript = () => {
    setTranscript('');
  };

  // Funções para captura de áudio
  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      const chunks: Blob[] = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        
        setAudioBlob(blob);
        setAudioUrl(url);
        setIsAudioRecording(false);
        
        // Parar todas as tracks do stream
        stream.getTracks().forEach(track => track.stop());
        
        console.log('Áudio gravado:', blob.size, 'bytes');
        toast.success('🎵 Áudio gravado com sucesso!');
      };
      
      recorder.onerror = (event) => {
        console.error('Erro na gravação de áudio:', event);
        toast.error('❌ Erro ao gravar áudio');
        setIsAudioRecording(false);
        stream.getTracks().forEach(track => track.stop());
      };
      
      setMediaRecorder(recorder);
      recorder.start();
      setIsAudioRecording(true);
      
      console.log('Gravação de áudio iniciada');
    } catch (error) {
      console.error('Erro ao iniciar gravação de áudio:', error);
      toast.error('❌ Erro ao acessar microfone');
      setIsAudioRecording(false);
    }
  };

  const stopAudioRecording = () => {
    if (mediaRecorder && isAudioRecording) {
      mediaRecorder.stop();
    }
  };

  const clearAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl('');
  };

  const saveAudioWithFeedback = async () => {
    if (!audioBlob || !selectedCard) {
      toast.error('❌ Nenhum áudio gravado para salvar');
      return;
    }

    try {
      // Criar FormData para enviar o áudio
      const formData = new FormData();
      formData.append('audio', audioBlob, `feedback_audio_${Date.now()}.webm`);
      formData.append('clienteFornecedorId', selectedCard.cliente.id);
      formData.append('transcript', transcript);
      formData.append('feedbackId', ''); // Será preenchido após criar o feedback

      // Enviar áudio para o servidor
      const response = await api.post('/upload/audio-feedback', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('🎵 Áudio salvo com sucesso!');
      
      // Limpar áudio após salvar
      clearAudio();
      
      const audioId = response.data.data?.id || response.data.data?.audioId;
      return audioId;
    } catch (error: any) {
      console.error('Erro ao salvar áudio:', error);
      toast.error(error.response?.data?.message || '❌ Erro ao salvar áudio');
      return null;
    }
  };

  // Função para mudar etapa do card através do modal
  const handleChangeEtapa = async () => {
    if (!selectedCard || !selectedEtapaId) return;
    
    try {
      setIsChangingEtapa(true);
      await updateCardEtapa(selectedCard.id, selectedEtapaId);
      
      // Fechar modal e atualizar dados
      setIsModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['kanban-simple'] });
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-estatisticas'] });
      
      toast.success('Etapa alterada com sucesso!');
    } catch (error) {
      console.error('Erro ao alterar etapa:', error);
      toast.error('Erro ao alterar etapa. Tente novamente.');
    } finally {
      setIsChangingEtapa(false);
    }
  };



  // Função para carregar usuários operadores
  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await api.get('/users?role=OPERADOR&isActive=true&limit=500');
      setUsers(response.data.data.users || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar lista de usuários');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Função para carregar operadores para filtro
  const loadOperadores = async () => {
    try {
      setLoadingOperadores(true);
      const response = await api.get('/users?role=OPERADOR&isActive=true&limit=500');
      setOperadores(response.data.data.users || []);
    } catch (error) {
      console.error('Erro ao carregar operadores:', error);
      toast.error('Erro ao carregar lista de operadores');
    } finally {
      setLoadingOperadores(false);
    }
  };

  // Função para carregar empresas para filtro
  const loadCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await api.get('/companies?isActive=true&limit=500');
      setCompanies(response.data.data.companies || []);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error('Erro ao carregar lista de empresas');
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Função para transferir card para um usuário
  const handleTransferUser = async () => {
    if (!selectedCard || !selectedUserId || !selectedCard.cliente?.id) return;
    
    try {
      setIsTransferringUser(true);
      
      const selectedUser = users.find(u => u.id === selectedUserId);
      const titulos = safeArray(selectedCard.titulos);
      const companyId =
        selectedCard.companyId ||
        (titulos[0] as { companyId?: string })?.companyId ||
        (titulos[0] as { company?: { id?: string } })?.company?.id;

      const response = await api.patch(
        `/titulos/cliente/${selectedCard.cliente.id}/assign-operator`,
        {
          operadorId: selectedUserId,
          ...(companyId ? { companyId } : {}),
        }
      );

      const updatedCount = response.data?.data?.updated ?? titulos.length;
      
      if (selectedUser) {
        try {
          await NotificationService.createLembrete({
            tituloId: (titulos[0] as any)?.id,
            titulo: `Card transferido para você`,
            descricao: `O cliente "${selectedCard.cliente?.nome}" foi transferido para você com ${updatedCount} título(s) ativo(s) no valor total de ${formatCurrency(titulos.reduce((sum: number, t: any) => sum + Number(t.saldo || t.valor), 0))}`,
            dataLembrete: new Date().toISOString(),
            userId: selectedUserId
          });
        } catch (notificationError) {
          console.error('Erro ao criar notificação:', notificationError);
        }
      }
      
      setIsModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['kanban-simple'] });
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-estatisticas'] });
      
      toast.success(`Card transferido com sucesso para ${selectedUser?.name || 'o operador'}! (${updatedCount} título(s))`);
    } catch (error) {
      console.error('Erro ao transferir card:', error);
      toast.error('Erro ao transferir card. Tente novamente.');
    } finally {
      setIsTransferringUser(false);
    }
  };

  // Funções para seleção de cards
  const handleCardSelect = useCallback((cardId: string, selected: boolean) => {
    setSelectedCards((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(cardId);
      } else {
        newSet.delete(cardId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback((etapaId: string, selected: boolean) => {
    if (!kanbanData?.data?.kanban) return;
    const etapaCards = safeArray(kanbanData.data.kanban[etapaId]);
    setSelectedCards((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        etapaCards.forEach((card: any) => newSet.add(card.id));
      } else {
        etapaCards.forEach((card: any) => newSet.delete(card.id));
      }
      return newSet;
    });
  }, [kanbanData]);

  const handleOpenWhatsAppModal = () => {
    if (selectedCards.size === 0) {
      toast.error('Selecione pelo menos um card para enviar mensagem');
      return;
    }
    setIsWhatsAppModalOpen(true);
  };

  const handleCardClick = async (card: any) => {
    setSelectedCard(card);
    setActiveTab('feedback');
    setIsModalOpen(true);
    
    // Carregar feedbacks do cliente
    try {
      setLoadingFeedbacks(true);
      const response = await api.get(`/titulos/feedback/cliente/${card.cliente.id}`);
      console.log('Resposta dos feedbacks:', response.data);
      // Verificar se a resposta tem a estrutura esperada
      const feedbacksData = response.data?.data?.feedbacks || response.data?.data || [];
      setFeedbacks(feedbacksData);
    } catch (error) {
      console.error('Erro ao carregar feedbacks:', error);
      setFeedbacks([]);
    } finally {
      setLoadingFeedbacks(false);
    }

    // Carregar comprovantes existentes
    try {
      const comprovantesResponse = await api.get(`/upload/comprovantes/cliente/${card.cliente.id}`);
      setUploadedFiles(comprovantesResponse.data.data.comprovantes || []);
    } catch (error) {
      console.error('Erro ao carregar comprovantes:', error);
      setUploadedFiles([]);
    }


    
    // Carregar usuários operadores
    loadUsers();
  };





  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.descricao.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }

    try {
      // Salvar áudio primeiro se existir
      let audioId = null;
      if (audioBlob) {
        audioId = await saveAudioWithFeedback();
      }

      // Registrar feedback
      const feedbackData = {
        clienteFornecedorId: selectedCard.cliente.id,
        tituloId: formData.titulosSelecionados.length > 0 ? formData.titulosSelecionados[0] : null, // Manter compatibilidade
        titulosNegociados: formData.titulosSelecionados.length > 0 ? formData.titulosSelecionados : undefined,
        tipo: formData.tipo,
        descricao: formData.descricao,
        tipoNegociacao: formData.tipoNegociacao || null,
        valorNegociado: formData.valorNegociado || null,
        proximoContato: formData.proximoContato ? new Date(formData.proximoContato).toISOString() : null,
        audioId: audioId, // Incluir ID do áudio se existir
        acaoGestor: formData.acaoGestor || false,
      };
      
      console.log('Enviando feedback:', feedbackData);
      await api.post('/titulos/feedback', feedbackData);

      // Se há próximo contato, criar lembrete
      if (formData.proximoContato && selectedCard.titulos.length > 0) {
        await NotificationService.createLembrete({
          tituloId: selectedCard.titulos[0].id,
          titulo: `Próximo Contato - ${selectedCard.cliente.nome}`,
          descricao: formData.descricao,
                        dataLembrete: new Date(formData.proximoContato).toISOString(),
        });
      }

      toast.success('Feedback registrado com sucesso!');
      
      // Limpar formulário e áudio
      setFormData({
        tipo: 'CONTATO',
        descricao: '',
        proximoContato: '',
        valorNegociado: '',
        tipoNegociacao: '',
        tituloId: '',
        titulosSelecionados: [],
        acaoGestor: false,
      });
      clearAudio();
      setTranscript('');

      // Recarregar feedbacks
      const response = await api.get(`/titulos/feedback/cliente/${selectedCard.cliente.id}`);
      const feedbacksData = response.data?.data?.feedbacks || response.data?.data || [];
      console.log('Feedbacks carregados:', feedbacksData);
      
      // Debug: Verificar títulos negociados
      feedbacksData.forEach((feedback: any) => {
        if (feedback.titulosNegociados && feedback.titulosNegociados.length > 0) {
          console.log('Feedback com títulos negociados:', {
            id: feedback.id,
            titulosNegociados: feedback.titulosNegociados,
            count: feedback.titulosNegociados.length
          });
        }
      });
      
      setFeedbacks(feedbacksData);

      // Atualizar Kanban instantaneamente
      await queryClient.invalidateQueries({ queryKey: ['kanban-simple'] });
      await queryClient.invalidateQueries({ queryKey: ['kanban'] });
      await queryClient.invalidateQueries({ queryKey: ['kanban-estatisticas'] });
      
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao registrar feedback');
    }
  };

  // Funções para gerenciar upload de arquivos
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setUploadingFiles(true);
      const formData = new FormData();
      
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });
      
      formData.append('clienteFornecedorId', selectedCard.cliente.id);
      formData.append('feedbackId', ''); // Será preenchido após criar o feedback

      const response = await api.post('/upload/comprovantes', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadedFiles(prev => [...prev, ...response.data.data.files]);
      setSelectedFiles([]);
      toast.success('Comprovantes enviados com sucesso!');
    } catch (error: any) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error.response?.data?.message || 'Erro ao enviar comprovantes');
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeUploadedFile = async (fileId: string) => {
    try {
      await api.delete(`/upload/comprovantes/${fileId}`);
      setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
      toast.success('Arquivo removido com sucesso!');
    } catch (error: any) {
      console.error('Erro ao remover arquivo:', error);
      toast.error(error.response?.data?.message || 'Erro ao remover arquivo');
    }
  };

  const removeFeedbackFile = async (fileId: string) => {
    try {
      await api.delete(`/upload/comprovantes/${fileId}`);
      // Recarregar feedbacks para atualizar a lista
      const response = await api.get(`/titulos/feedback/cliente/${selectedCard.cliente.id}`);
      const feedbacksData = response.data?.data?.feedbacks || response.data?.data || [];
      setFeedbacks(feedbacksData);
      toast.success('Arquivo removido com sucesso!');
    } catch (error: any) {
      console.error('Erro ao remover arquivo:', error);
      toast.error(error.response?.data?.message || 'Erro ao remover arquivo');
    }
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return '🖼️';
      case 'doc':
      case 'docx':
        return '📝';
      default:
        return '📎';
    }
  };


  /**
   * Abre o visualizador de arquivo em uma nova aba
   * 
   * Versão: 2.0.1
   * Data: 2025-01-XX
   * 
   * Alterações:
   * - Usa getFileUrl() para construir URL completa do arquivo
   * - Suporta URLs relativas e absolutas
   * - Tratamento de erros melhorado
   * 
   * @param fileUrl - URL do arquivo (relativa ou absoluta)
   * @param fileName - Nome do arquivo para exibição em mensagens de erro
   */
  const openFileViewer = (fileUrl: string, fileName: string) => {
    try {
      const fullUrl = getFileUrl(fileUrl);
      console.log('Abrindo arquivo:', { fileUrl, fullUrl, fileName });
      
      // Tentar abrir o arquivo em nova aba
      const newWindow = window.open(fullUrl, '_blank');
      
      if (!newWindow) {
        console.error('Não foi possível abrir a janela');
        toast.error(`Não foi possível abrir o arquivo: ${fileName}`);
      }
    } catch (error) {
      console.error('Erro ao abrir arquivo:', error);
      toast.error(`Erro ao visualizar o arquivo: ${fileName}`);
    }
  };

  /**
   * Gera preview do arquivo baseado na extensão
   * 
   * Versão: 2.0.1
   * Data: 2025-01-XX
   * 
   * Alterações:
   * - Imagens agora usam getFileUrl() para construir URL completa
   * - Imagens são clicáveis e abrem o visualizador
   * - Tratamento de erro melhorado com fallback visual
   * 
   * @param file - Objeto do arquivo com fileName e url
   * @returns Componente React com preview do arquivo
   */
  const getFilePreview = (file: any) => {
    const extension = file.fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return (
          <div className="w-full h-32 bg-muted rounded-lg flex items-center justify-center border border-border">
            <div className="text-center">
              <div className="text-3xl mb-2">📄</div>
              <div className="text-xs text-muted-foreground">PDF</div>
              <div className="text-xs text-muted-foreground">{file.fileName}</div>
            </div>
          </div>
        );
      case 'jpg':
      case 'jpeg':
      case 'png':
        // Usar getFileUrl() para garantir URL completa e correta
        const imageUrl = getFileUrl(file.url);
        return (
          <div className="w-full h-32 bg-muted rounded-lg overflow-hidden border border-border">
            <img 
              src={imageUrl} 
              alt={file.fileName}
              className="w-full h-full object-cover cursor-pointer"
              onClick={() => openFileViewer(file.url, file.fileName)}
              onError={(e) => {
                // Fallback visual caso a imagem não carregue
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = `
                    <div class="w-full h-full flex items-center justify-center cursor-pointer" onclick="window.open('${imageUrl}', '_blank')">
                      <div class="text-center">
                        <div class="text-3xl mb-2">🖼️</div>
                        <div class="text-xs text-muted-foreground">Imagem</div>
                        <div class="text-xs text-muted-foreground">${file.fileName}</div>
                        <div class="text-xs text-blue-500 mt-1">Clique para visualizar</div>
                      </div>
                    </div>
                  `;
                }
              }}
            />
          </div>
        );
      case 'doc':
      case 'docx':
        return (
          <div className="w-full h-32 bg-muted rounded-lg flex items-center justify-center border border-border">
            <div className="text-center">
              <div className="text-3xl mb-2">📝</div>
              <div className="text-xs text-muted-foreground">Documento</div>
              <div className="text-xs text-muted-foreground">{file.fileName}</div>
            </div>
          </div>
        );
      default:
        return (
          <div className="w-full h-32 bg-muted rounded-lg flex items-center justify-center border border-border">
            <div className="text-center">
              <div className="text-3xl mb-2">📎</div>
              <div className="text-xs text-muted-foreground">Arquivo</div>
              <div className="text-xs text-muted-foreground">{file.fileName}</div>
            </div>
          </div>
        );
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };



  const getFeedbackIcon = (tipo: string) => {
    switch (tipo) {
      case 'LIGACAO':
        return <Phone className="h-4 w-4" />;
      case 'EMAIL':
        return <Mail className="h-4 w-4" />;
      case 'WHATSAPP':
        return <MessageSquare className="h-4 w-4" />;
      case 'VISITA':
        return <MapPin className="h-4 w-4" />;
      case 'GESTOR_RESPONSE':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getFeedbackColor = (tipo: string) => {
    switch (tipo) {
      case 'LIGACAO':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'EMAIL':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800';
      case 'WHATSAPP':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800 dark:bg-green-500/20 dark:text-green-200 dark:border-green-800';
      case 'VISITA':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'GESTOR_RESPONSE':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-muted text-gray-800 border-border';
    }
  };

  const formatTipoNegociacao = (tipo: string) => {
    switch (tipo) {
      case 'NEGOCIADO':
        return 'Negociado';
      case 'PAGO':
        return 'Pago';
      case 'NEGOCIADO_EM_LOJA':
        return 'Negociado em Loja';
      case 'ENTRADA_PAGA_EM_LOJA':
        return 'Entrada Paga em Loja';
      case 'GESTOR_ACEITOU':
        return 'Aceito pelo Gestor';
      case 'GESTOR_NEGOU':
        return 'Negado pelo Gestor';
      default:
        return tipo;
    }
  };

  // Função para agrupar títulos por status
  const groupTitulosByStatus = (titulos: any[]) => {
    return titulos.reduce((groups, titulo) => {
      const statusEfetivo = effectiveTituloStatus(titulo.status, titulo.vencimento);
      let status = 'PAGO';

      if (statusEfetivo === 'VENCIDO') {
        status = 'VENCIDO';
      } else if (statusEfetivo === 'ABERTO') {
        status = 'A_VENCER';
      } else if (statusEfetivo === 'PARCIAL') {
        status = 'PARCIAL';
      }
      
      if (!groups[status]) {
        groups[status] = [];
      }
      groups[status].push(titulo);
      return groups;
    }, {} as Record<string, any[]>);
  };



  // Funções para drag and drop
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const cardId = active.id as string;
    const card = findCardById(cardId);
    setActiveCard(card);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over) {
      setActiveCard(null);
      return;
    }

    const cardId = active.id as string;
    const overId = over.id as string;
    
    console.log('Drag end - Card ID:', cardId, 'Over ID:', overId);
    
    // Extrair o ID da etapa do ID da coluna (formato: etapa-{etapaId})
    let newEtapaId: string;
    
    if (overId.startsWith('etapa-')) {
      newEtapaId = overId.replace('etapa-', '');
    } else {
      // Drop em outro card: resolver a coluna desse card
      const overCardEtapa = findCardEtapa(overId);
      if (!overCardEtapa) {
        setActiveCard(null);
        return;
      }
      newEtapaId = overCardEtapa;
    }
    
    console.log('Nova Etapa ID extraída:', newEtapaId);
    
    // Verificar se o newEtapaId é válido
    if (!newEtapaId || typeof newEtapaId !== 'string') {
      console.error('ID da etapa inválido:', newEtapaId);
      toast.error('Etapa inválida. Tente novamente.');
      setActiveCard(null);
      return;
    }
    
    // Verificar se o card está sendo movido para uma etapa diferente
    const currentEtapa = findCardEtapa(cardId);
    if (currentEtapa === newEtapaId) {
      console.log('Card já está na etapa correta');
      setActiveCard(null);
      return;
    }
    
    try {
      // Atualizar a etapa do card no backend
      await updateCardEtapa(cardId, newEtapaId);
      
      // Invalidar e recarregar os dados do kanban
      await queryClient.invalidateQueries({ queryKey: ['kanban-simple'] });
      
      toast.success('Card movido com sucesso!');
    } catch (error: any) {
      console.error('Erro detalhado ao mover card:', error);
      
      let errorMessage = 'Erro ao mover card';
      
      if (error.message) {
        if (error.message.includes('Etapa do Kanban não encontrada')) {
          errorMessage = 'Etapa do Kanban não encontrada. Tente recarregar a página.';
        } else if (error.message.includes('Título') && error.message.includes('não encontrado')) {
          errorMessage = 'Alguns títulos não foram encontrados. Tente recarregar a página.';
        } else if (error.message.includes('Card não encontrado')) {
          errorMessage = 'Card não encontrado. Tente recarregar a página.';
        } else {
          errorMessage = error.message;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setActiveCard(null);
    }
  };

  const findCardEtapa = (cardId: string) => {
    const etapas = safeArray((kanbanData as any)?.data?.etapas);
    const kanban = safeObject((kanbanData as any)?.data?.kanban);
    
    for (const etapa of etapas) {
      const cards = safeArray(kanban[(etapa as any).id]);
      const card = cards.find((c: any) => c.id === cardId);
      if (card) return (etapa as any).id;
    }
    return null;
  };

  const findCardById = (cardId: string) => {
    const etapas = safeArray((kanbanData as any)?.data?.etapas);
    const kanban = safeObject((kanbanData as any)?.data?.kanban);
    
    console.log('Procurando card com ID:', cardId);
    console.log('Etapas disponíveis:', etapas.map((e: any) => e.id));
    
    for (const etapa of etapas) {
      const cards = safeArray(kanban[(etapa as any).id]);
      console.log(`Etapa ${(etapa as any).id}: ${cards.length} cards`);
      
      const card = cards.find((c: any) => c.id === cardId);
      if (card) {
        console.log('Card encontrado:', card);
        return card;
      }
    }
    
    console.log('Card não encontrado para ID:', cardId);
    return null;
  };

  const updateCardEtapa = async (cardId: string, newEtapaId: string) => {
    console.log('=== INICIANDO UPDATE CARD ETAPA ===');
    console.log('Card ID:', cardId);
    console.log('Nova Etapa ID:', newEtapaId);
    
    // Verificar se a etapa existe
    const etapas = safeArray((kanbanData as any)?.data?.etapas);
    console.log('Total de etapas carregadas:', etapas.length);
    
    // Log detalhado das etapas
    etapas.forEach((etapa: any, index: number) => {
      console.log(`Etapa ${index + 1}:`, {
        id: (etapa as any).id,
        nome: (etapa as any).nome,
        ordem: (etapa as any).ordem,
        isMatch: (etapa as any).id === newEtapaId
      });
    });
    
    const etapaExiste = etapas.some((etapa: any) => (etapa as any).id === newEtapaId);
    console.log('Etapa encontrada:', etapaExiste);
    
    if (!etapaExiste) {
      console.error('=== ERRO: ETAPA NÃO ENCONTRADA ===');
      console.error('Etapa solicitada:', newEtapaId);
      console.error('Tipo da etapa solicitada:', typeof newEtapaId);
      console.error('Etapas disponíveis:');
      etapas.forEach((etapa: any, index: number) => {
        console.error(`  ${index + 1}. ID: ${(etapa as any).id} | Tipo: ${typeof (etapa as any).id} | Nome: ${(etapa as any).nome}`);
      });
      console.error('====================================');
      throw new Error(`Etapa do Kanban não encontrada: ${newEtapaId}`);
    }

    // Buscar o card para obter os títulos
    const card = findCardById(cardId);
    if (!card || !(card as any).titulos || (card as any).titulos.length === 0) {
      throw new Error('Card não encontrado ou sem títulos');
    }

    // Verificar se todos os títulos têm IDs válidos
    const titulos = (card as any).titulos.filter((titulo: any) => titulo && titulo.id);
    if (titulos.length === 0) {
      throw new Error('Nenhum título válido encontrado no card');
    }

    console.log(`Movendo ${titulos.length} títulos para etapa: ${newEtapaId}`);

    // Atualizar a etapa de todos os títulos do card
    const updatePromises = titulos.map((titulo: any) => {
      console.log(`Atualizando título ${titulo.id} para etapa ${newEtapaId}`);
      return api.patch(`/titulos/${titulo.id}/kanban-etapa`, {
        kanbanEtapaId: newEtapaId
      }).catch((error: any) => {
        console.error(`Erro ao atualizar título ${titulo.id}:`, error);
        if (error.response?.status === 404) {
          throw new Error(`Título ${titulo.id} não encontrado no servidor`);
        }
        throw error;
      });
    });

    await Promise.all(updatePromises);
  };

  const handleListMoveEtapa = async (cardId: string, newEtapaId: string) => {
    try {
      setListMovingCardId(cardId);
      await updateCardEtapa(cardId, newEtapaId);
      await queryClient.invalidateQueries({ queryKey: ['kanban-simple'] });
      toast.success('Etapa alterada com sucesso!');
    } catch (error: any) {
      console.error('Erro ao mover card na lista:', error);
      toast.error(error?.message || 'Erro ao alterar etapa');
      throw error;
    } finally {
      setListMovingCardId(null);
    }
  };

  // Extrair dados do kanban
  const etapas = useMemo(() => safeArray((kanbanData as any)?.data?.etapas), [kanbanData]);
  const kanban = useMemo(() => safeObject((kanbanData as any)?.data?.kanban), [kanbanData]);

  const columnsByEtapa = useMemo(() => {
    const result: Record<string, any[]> = {};
    for (const etapa of etapas) {
      const cards = safeArray(kanban[(etapa as any).id]);
      const cardsFiltrados = filterCardsByUserRole(cards);
      result[(etapa as any).id] = ordenarKanbanCards(cardsFiltrados, sortBy, sortOrder);
    }
    return result;
  }, [etapas, kanban, user, sortBy, sortOrder]);

  // Verificar duplicação de cards por cliente/fornecedor
  const checkDuplicateCards = () => {
    const allCards: any[] = [];
    const duplicates: any[] = [];
    
    Object.values(kanban).forEach((cards: any) => {
      safeArray(cards).forEach((card: any) => {
        allCards.push(card);
      });
    });

    const clienteIds = allCards.map(card => card.cliente?.id).filter(Boolean);
    const uniqueIds = new Set(clienteIds);
    
    if (clienteIds.length !== uniqueIds.size) {
      // Encontrar duplicatas
      const counts: Record<string, number> = {};
      clienteIds.forEach(id => {
        counts[id] = (counts[id] || 0) + 1;
      });
      
      Object.entries(counts).forEach(([id, count]) => {
        if (count > 1) {
          const duplicateCards = allCards.filter(card => card.cliente?.id === id);
          duplicates.push(...duplicateCards);
        }
      });
    }

    return { hasDuplicates: duplicates.length > 0, duplicates };
  };

  const { hasDuplicates, duplicates } = checkDuplicateCards();

/** Classe compartilhada dos filtros do header — evita truncar rótulos no select nativo. */
const KANBAN_FILTER_SELECT_CLASS =
  'h-9 min-w-[10.5rem] w-full sm:w-auto sm:min-w-[11.5rem] lg:min-w-[12rem] max-w-full text-sm shrink-0';

  return (
    <div className="h-full flex flex-col w-full" data-testid="page-kanban">
      {/* Header fixo */}
      <div className="flex-shrink-0 border-b border-border bg-card p-3 dark:border-slate-700 dark:bg-slate-800 sm:p-4 sm:pb-3 w-full min-w-0">
        <div className="flex flex-col gap-3">
          {/* Linha 1: título + atualizar */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
            <h1 className="min-w-0 flex-1 truncate text-xl font-bold text-foreground sm:text-2xl lg:text-3xl">
              {selectedKanban?.nome || 'Kanban de Cobranças'}
            </h1>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  refetch();
                  toast.success('Kanban atualizado!');
                }}
                disabled={isFetching}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                <span>Atualizar</span>
              </Button>
              {isFetching && kanbanData && (
                <span className="hidden text-xs text-gray-500 animate-pulse dark:text-slate-400 sm:inline">
                  Atualizando...
                </span>
              )}
            </div>
          </div>

          {/* Linha 2: ordenação, status e filtros */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {user?.role === 'OPERADOR' && (
              <Badge
                variant="secondary"
                className="shrink-0 cursor-pointer border-0 bg-gradient-to-r from-blue-500 to-purple-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:from-blue-600 hover:to-purple-700 hover:shadow-md"
                onClick={() => setIsStatsOpen(true)}
              >
                📊 Estatísticas
              </Badge>
            )}

            <div className="flex shrink-0 items-center rounded-lg bg-gray-100 p-1 dark:bg-slate-700">
              <Button
                variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                size="sm"
                title="Visualização em colunas (Kanban)"
                onClick={() => setViewModePersist('kanban')}
                className="flex items-center gap-1.5 whitespace-nowrap px-2 sm:px-3"
              >
                <LayoutGrid className="h-4 w-4 shrink-0" />
                <span className="hidden md:inline">Kanban</span>
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                title="Visualização em lista"
                onClick={() => setViewModePersist('list')}
                className="flex items-center gap-1.5 whitespace-nowrap px-2 sm:px-3"
              >
                <List className="h-4 w-4 shrink-0" />
                <span className="hidden md:inline">Lista</span>
              </Button>
            </div>

            <div className="flex shrink-0 items-center rounded-lg bg-gray-100 p-1 dark:bg-slate-700">
              <Button
                variant={sortBy === 'diasAtraso' ? 'default' : 'ghost'}
                size="sm"
                title="Ordenar por dias de atraso"
                onClick={() => {
                  if (sortBy === 'diasAtraso') {
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortBy('diasAtraso');
                    setSortOrder('desc');
                  }
                }}
                className="flex items-center gap-1.5 whitespace-nowrap px-2 sm:px-3"
              >
                <Calendar className="h-4 w-4 shrink-0" />
                <span className="hidden md:inline">Dias Atraso</span>
                {sortBy === 'diasAtraso' &&
                  (sortOrder === 'asc' ? (
                    <ArrowUp className="h-3 w-3 shrink-0" />
                  ) : (
                    <ArrowDown className="h-3 w-3 shrink-0" />
                  ))}
              </Button>
              <Button
                variant={sortBy === 'valorVencido' ? 'default' : 'ghost'}
                size="sm"
                title="Ordenar por valor vencido"
                onClick={() => {
                  if (sortBy === 'valorVencido') {
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortBy('valorVencido');
                    setSortOrder('desc');
                  }
                }}
                className="flex items-center gap-1.5 whitespace-nowrap px-2 sm:px-3"
              >
                <DollarSign className="h-4 w-4 shrink-0" />
                <span className="hidden md:inline">Valor Vencido</span>
                {sortBy === 'valorVencido' &&
                  (sortOrder === 'asc' ? (
                    <ArrowUp className="h-3 w-3 shrink-0" />
                  ) : (
                    <ArrowDown className="h-3 w-3 shrink-0" />
                  ))}
              </Button>
            </div>

            <div className="hidden shrink-0 items-center gap-1.5 text-xs text-muted-foreground lg:flex">
              <div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
              <span className="whitespace-nowrap">Auto-atualização a cada 60s</span>
            </div>

            <div className="flex w-full flex-wrap items-stretch gap-2 sm:w-auto sm:flex-1 sm:justify-end xl:flex-none">
              <div className="relative min-w-[12rem] flex-1 sm:max-w-xs sm:flex-none">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={clienteSearchInput}
                  onChange={(e) => setClienteSearchInput(e.target.value)}
                  placeholder="Buscar cliente (nome, placa ou CPF)..."
                  aria-label="Buscar cliente por nome, placa ou CPF"
                  title="Filtra o Kanban via API. Ctrl+F do navegador não encontra cards fora da área visível (colunas virtualizadas)."
                  className="h-9 pl-8 pr-8 text-sm"
                />
                {clienteSearchInput ? (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setClienteSearchInput('');
                      setClienteSearch('');
                    }}
                    aria-label="Limpar busca"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
                <p className="mt-0.5 hidden text-[10px] leading-tight text-muted-foreground sm:block">
                  Use esta busca — Ctrl+F não cobre colunas virtualizadas
                </p>
              </div>

              {(user?.role === 'ADMIN' || user?.role === 'GESTOR') && (
                <Select
                  value={selectedOperador}
                  onValueChange={setSelectedOperador}
                  disabled={loadingOperadores}
                  className={KANBAN_FILTER_SELECT_CLASS}
                >
                  <option value="">Todos os Operadores</option>
                  {operadores.map((operador) => (
                    <option key={operador.id} value={operador.id}>
                      {operador.name}
                    </option>
                  ))}
                </Select>
              )}

              {(user?.role === 'ADMIN' || user?.role === 'GESTOR') && (
                <Select
                  value={selectedCompany}
                  onValueChange={setSelectedCompany}
                  disabled={loadingCompanies}
                  className={KANBAN_FILTER_SELECT_CLASS}
                >
                  <option value="">Todas as Empresas</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Select>
              )}

              {(user?.role === 'ADMIN' || user?.role === 'GESTOR') && (
                <Select
                  value={selectedGestorAction}
                  onValueChange={setSelectedGestorAction}
                  className={KANBAN_FILTER_SELECT_CLASS}
                >
                  <option value="">Todas as Ações do Gestor</option>
                  <option value="ACEITO">Aceito pelo Gestor</option>
                  <option value="NEGADO">Negado pelo Gestor</option>
                </Select>
              )}

              <Select
                value={selectedAtrasoFilter}
                onValueChange={setSelectedAtrasoFilter}
                className={KANBAN_FILTER_SELECT_CLASS}
              >
                <option value="">Todos os atrasos</option>
                <option value="1-2">1 a 2 dias</option>
                <option value="3-6">3 a 6 dias</option>
                <option value="7+">Acima de 7 dias</option>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="flex-1 overflow-hidden w-full min-w-0">
        <LoadingState isLoading={isLoading && !kanbanData}>
          {!selectedKanbanId ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Selecione um Kanban
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Escolha um kanban na barra lateral para visualizar os títulos
                </p>
              </div>
            </div>
          ) : error ? (
            <AuthErrorState 
              error={error as Error} 
              onRetry={handleRefresh}
            />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <div className="h-full flex flex-col w-full min-w-0">

                {/* Alert de duplicação se houver */}
                {hasDuplicates && (
                  <div className="flex-shrink-0 px-6 pb-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                        <div>
                          <h3 className="text-sm font-medium text-yellow-800">
                            Cards Duplicados Detectados
                          </h3>
                          <p className="text-sm text-yellow-700">
                            {duplicates.length} cards duplicados encontrados. Isso pode causar problemas no drag and drop.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Área do Kanban / Lista */}
                <div className="flex-1 overflow-hidden w-full">
                  {viewMode === 'list' ? (
                    <div className="h-full w-full min-h-0 p-2 sm:p-4 pt-0">
                      <KanbanCardsList
                        etapas={etapas as Array<{ id: string; nome: string; cor?: string }>}
                        cardsByEtapa={columnsByEtapa}
                        selectedCards={selectedCards}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onCardSelect={handleCardSelect}
                        onCardClick={handleCardClick}
                        onMoveEtapa={handleListMoveEtapa}
                        movingCardId={listMovingCardId}
                      />
                    </div>
                  ) : (
                    <div className="h-full overflow-x-auto w-full">
                      <div className="flex h-full space-x-3 min-w-max p-4 pt-0 w-full justify-start">
                        {etapas.map((etapa: any) => (
                          <DroppableColumn
                            key={etapa.id}
                            etapa={etapa}
                            cards={columnsByEtapa[etapa.id] ?? []}
                            selectedCards={selectedCards}
                            onSelectAll={handleSelectAll}
                            onCardSelect={handleCardSelect}
                            onCardClick={handleCardClick}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* Drag Overlay */}
                {viewMode === 'kanban' ? (
                  <DragOverlay>
                    {activeCard ? (
                      <div className="w-80">
                        <SortableCard card={activeCard} />
                      </div>
                    ) : null}
                  </DragOverlay>
                ) : null}
              </div>
            </DndContext>
          )}
        </LoadingState>
      </div>

      {/* Modal com Abas */}
      <KanbanCardModalLazy
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={(() => {
          const cliente = selectedCard?.cliente;
          if (!cliente) return 'Cliente - Detalhes';
          
          const empresa = cliente.empresa?.nome || '';
          const nome = cliente.nome || '';
          const whatsapp = cliente.whatsapp || cliente.telefone || cliente.celular || '';
          const email = cliente.email || '';
          let title = nome;
          if (empresa) title += ` (${empresa})`;
          if (whatsapp) title += ` - ${whatsapp}`;
          if (email) title += ` - ${email}`;
          return title;
        })()}
        size="2xl"
      >
        {/* Abas */}
        <div className="border-b border-border mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('feedback')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'feedback'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
              }`}
            >
              <MessageSquare className="w-4 h-4 inline mr-2" />
              Contatos
            </button>
            <button
              onClick={() => setActiveTab('titulos')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'titulos'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Títulos ({(() => {
                const titulos = safeArray(selectedCard?.titulos);
                const titulosAgrupados = groupTitulosByStatus(titulos);
                const titulosVencidos = titulosAgrupados['VENCIDO'] || [];
                const titulosParciais = titulosAgrupados['PARCIAL'] || [];
                return titulosVencidos.length + titulosParciais.length;
              })()})
            </button>
            <button
              onClick={() => setActiveTab('mudar-etapa')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'mudar-etapa'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
              }`}
            >
              <Move className="w-4 h-4 inline mr-2" />
              Mudar Etapa
            </button>
            {(user?.role === 'ADMIN' || user?.role === 'GESTOR') && (
            <button
              onClick={() => setActiveTab('transferir-usuario')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'transferir-usuario'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Transferir Usuário
            </button>
            )}
            <button
              onClick={() => setActiveTab('transferir-kanban')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'transferir-kanban'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
              }`}
            >
              <ArrowRight className="w-4 h-4 inline mr-2" />
              Transferir Kanban
            </button>
            <button
              onClick={() => setActiveTab('whatsapp')}
              className={`py-2 px-1 border-b-2 font-medium text-sm relative ${
                activeTab === 'whatsapp'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
              }`}
            >
              <MessageSquare className="w-4 h-4 inline mr-2" />
              WhatsApp Chat
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('timeline')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'timeline'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-foreground hover:border-gray-300 dark:border-slate-600'
              }`}
            >
              <Clock className="w-4 h-4 inline mr-2" />
              Time-line do status
            </button>

          </nav>
        </div>

        {/* Conteúdo das Abas */}
        {activeTab === 'feedback' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
            {/* Coluna 1: Títulos Relacionados */}
            <div className="lg:col-span-1">
              <div className="mb-2">
                <label className="block text-sm font-medium mb-2 text-foreground">Títulos relacionados (opcional)</label>
                <div className="max-h-[600px] overflow-auto border border-border rounded p-2 space-y-2 bg-card">
                  {(Array.isArray(selectedCard?.titulos) ? selectedCard!.titulos : []).length > 0 ? (
                    (Array.isArray(selectedCard?.titulos) ? selectedCard!.titulos : [])
                      .filter(
                        (t: any) =>
                          effectiveTituloStatus(t.status, t.vencimento) === 'VENCIDO' || t.status === 'PARCIAL'
                      )
                      .map((t: any) => {
                        const checked = formData.titulosSelecionados.includes(t.id);
                        return (
                          <label key={t.id} className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...formData.titulosSelecionados, t.id]
                                  : formData.titulosSelecionados.filter((id) => id !== t.id);
                                setFormData({ ...formData, titulosSelecionados: next });
                              }}
                              className="rounded"
                            />
                            <span className="text-gray-700 dark:text-gray-300">
                              <div className="flex flex-col space-y-1">
                                <div className="font-medium">
                                  ID: {t.numero} - {t.company?.name || 'Empresa'}
                                </div>
                                <div className="text-xs space-y-1">
                                  <div>Vencimento: {formatDate(t.vencimento)}</div>
                                  <div>Valor Total: {formatCurrency(t.valor)}</div>
                                  {t.valorPago && t.valorPago > 0 && (
                                    <div className="text-green-600">Valor Pago: {formatCurrency(t.valorPago)}</div>
                                  )}
                                  <div className="font-semibold text-blue-600">Saldo: {formatCurrency(t.saldo || t.valor)}</div>
                                  <div className="text-xs bg-muted px-2 py-1 rounded inline-block">
                                    Status: {t.status}
                                  </div>
                                </div>
                              </div>
                            </span>
                          </label>
                        );
                      })
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Nenhum título vencido ou parcial disponível para este cliente</p>
                  )}
                </div>
                {formData.titulosSelecionados.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.titulosSelecionados.length} título(s) selecionado(s)
                  </p>
                )}
              </div>
            </div>

            {/* Colunas 2 e 3: Formulário e Histórico */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
            {/* Formulário de Feedback */}
            <div className="flex flex-col h-full">
              <h3 className="text-lg font-semibold mb-4 text-foreground">Novo Contato</h3>
              <form onSubmit={handleSubmitFeedback} className="space-y-4 flex-1 overflow-y-auto">

                {/* Informações dos Títulos Selecionados - Exibidas após selecionar títulos */}
                {formData.titulosSelecionados.length > 0 && (() => {
                  const selectedTitulos = (Array.isArray(selectedCard?.titulos) ? selectedCard!.titulos : [])
                    .filter((t: any) => formData.titulosSelecionados.includes(t.id));
                  
                  if (selectedTitulos.length > 0) {
                    return (
                      <div className="mb-4 space-y-3">
                        {selectedTitulos.map((selectedTitulo: any) => (
                          <div key={selectedTitulo.id} className="border rounded-lg p-3 bg-muted/50">
                            <h4 className="font-medium text-sm mb-2 text-foreground">
                              {selectedTitulo.numero} - {selectedTitulo.company?.name || 'Empresa'}
                            </h4>
                            
                            {/* Ações do Boleto */}
                            {selectedTitulo.urlBoleto && (
                          <div className="p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <div className="flex items-center space-x-1 mb-2">
                              <ExternalLink className="h-4 w-4 text-blue-600" />
                              <span className="font-medium text-blue-700 text-sm">Ações do Boleto</span>
                            </div>
                            <p className="text-blue-600 break-all mb-3 text-xs">
                              {selectedTitulo.urlBoleto}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                onClick={(e) => {
                                  e.preventDefault();
                                  copyToClipboard(selectedTitulo.urlBoleto);
                                }}
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 text-xs"
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                Copiar
                              </Button>
                              <Button
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.open(selectedTitulo.urlBoleto, '_blank');
                                }}
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 text-xs"
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                Abrir
                              </Button>
                              <Button
                                onClick={(e) => {
                                  e.preventDefault();
                                  sendToWhatsApp(selectedTitulo.urlBoleto, selectedTitulo.numero);
                                }}
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 text-xs bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 text-green-700 hover:bg-green-100"
                              >
                                <Send className="h-3 w-3 mr-1" />
                                WhatsApp
                              </Button>
                              <Button
                                onClick={(e) => {
                                  e.preventDefault();
                                  sendToEmail(selectedTitulo.urlBoleto, selectedTitulo.numero);
                                }}
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 text-xs bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 dark:hover:bg-blue-900/50"
                              >
                                <Mail className="h-3 w-3 mr-1" />
                                E-mail
                              </Button>
                            </div>
                          </div>
                        )}

                            {/* Observações do Título */}
                            <div className="p-3 bg-muted/50 border border-border rounded-lg">
                              <div className="flex items-center space-x-1 mb-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium text-foreground text-sm">Observações do Título</span>
                              </div>
                              <div className="text-muted-foreground text-xs">
                                {selectedTitulo.observacoes ? (
                                  <p className="break-words">{selectedTitulo.observacoes}</p>
                                ) : (
                                  <p className="text-gray-400 italic">Nenhuma observação registrada</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return null;
                })()}
                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Tipo de Contato</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {[
                      { value: 'CONTATO', label: 'Contato', icon: '📞', color: 'bg-blue-500 hover:bg-blue-600' },
                      { value: 'LIGACAO', label: 'Ligação', icon: '📱', color: 'bg-green-500 hover:bg-green-600' },
                      { value: 'EMAIL', label: 'Email', icon: '📧', color: 'bg-purple-500 hover:bg-purple-600' },
                      { value: 'WHATSAPP', label: 'WhatsApp', icon: '💬', color: 'bg-green-600 hover:bg-green-700' },
                      { value: 'VISITA', label: 'Visita', icon: '🏢', color: 'bg-orange-500 hover:bg-orange-600' },
                      { value: 'NEGOCIACAO', label: 'Negociação', icon: '🤝', color: 'bg-indigo-500 hover:bg-indigo-600' },
                      { value: 'PROMESSA', label: 'Promessa', icon: '✅', color: 'bg-emerald-500 hover:bg-emerald-600' }
                    ].map((tipo) => (
                      <button
                        key={tipo.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, tipo: tipo.value })}
                        className={`p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center justify-center space-y-1 text-xs font-medium ${
                          formData.tipo === tipo.value
                            ? `${tipo.color} text-white border-transparent shadow-md`
                            : 'bg-card text-foreground border-gray-300 hover:border-gray-400 hover:bg-muted/50'
                        }`}
                      >
                        <span className="text-sm">{tipo.icon}</span>
                        <span className="text-center leading-tight">{tipo.label}</span>
                      </button>
                    ))}
                  </div>
                </div>



                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Descrição *</label>
                  <div className="relative">
                    <textarea
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      className="w-full p-2 pr-12 border border-gray-300 dark:border-slate-600 rounded-md h-24"
                      placeholder="Descreva o contato realizado..."
                      required
                    />
                    <button
                      type="button"
                      onClick={toggleRecording}
                      className={`absolute right-2 top-2 p-2 rounded-full transition-all ${
                        isRecording 
                          ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse' 
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                      title={isRecording ? 'Parar gravação' : 'Iniciar gravação de voz'}
                    >
                      {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {/* Ação do Gestor */}


                  {/* Área de transcrição */}
                  {(transcript || isRecording) && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-medium text-blue-800">
                            {isRecording ? '🎤 Gravando...' : '📝 Transcrição:'}
                          </h4>
                          {isRecording && (
                            <div className="flex items-center space-x-1">
                              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                              <span className="text-xs text-red-600 font-medium">AO VIVO</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {transcript && (
                            <>
                              <button
                                type="button"
                                onClick={applyTranscript}
                                className="text-xs bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition-colors font-medium"
                              >
                                ✅ Aplicar
                              </button>
                              <button
                                type="button"
                                onClick={clearTranscript}
                                className="text-xs bg-muted/50 text-white px-3 py-1 rounded hover:bg-gray-600 transition-colors"
                              >
                                🗑️ Limpar
                              </button>
                            </>
                          )}
                          {isRecording && (
                            <button
                              type="button"
                              onClick={stopRecording}
                              className="text-xs bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors font-medium"
                            >
                              ⏹️ Parar
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {transcript ? (
                        <div className="text-sm text-blue-700 bg-card p-3 rounded border min-h-[60px]">
                          {transcript}
                        </div>
                      ) : isRecording ? (
                        <div className="text-sm text-blue-600 bg-card p-3 rounded border min-h-[60px] flex items-center justify-center">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                            <span>Aguardando fala...</span>
                          </div>
                        </div>
                      ) : null}
                      
                      {isRecording && (
                        <div className="mt-2 text-xs text-blue-600">
                          💡 Dica: Fale claramente e próximo ao microfone para melhor precisão
                        </div>
                      )}
                    </div>
                  )}

                  {/* Área de áudio gravado */}
                  {audioUrl && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-medium text-green-800">🎵 Áudio Gravado:</h4>
                          <span className="text-xs text-green-600 font-medium">
                            {audioBlob ? `${(audioBlob.size / 1024).toFixed(1)} KB` : ''}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={clearAudio}
                            className="text-xs bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors"
                          >
                            🗑️ Remover
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-card p-3 rounded border">
                        <audio 
                          controls 
                          className="w-full"
                          src={audioUrl}
                        >
                          Seu navegador não suporta o elemento de áudio.
                        </audio>
                        <div className="mt-2 text-xs text-muted-foreground">
                          💡 Este áudio será salvo automaticamente quando você enviar o feedback
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Próximo Contato</label>
                  <input
                    type="datetime-local"
                    value={formData.proximoContato}
                    onChange={(e) => setFormData({ ...formData, proximoContato: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-slate-600 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Valor Negociado</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valorNegociado}
                    onChange={(e) => setFormData({ ...formData, valorNegociado: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-slate-600 rounded-md"
                    placeholder="0,00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Tipo de Negociação</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {[
                      { value: '', label: 'Nenhum', icon: '❌', color: 'bg-muted/50 hover:bg-gray-600' },
                      { value: 'NEGOCIADO', label: 'Negociado', icon: '🤝', color: 'bg-blue-500 hover:bg-blue-600' },
                      { value: 'PAGO', label: 'Pago', icon: '💰', color: 'bg-green-500 hover:bg-green-600' },
                      { value: 'NEGOCIADO_EM_LOJA', label: 'Negociado em Loja', icon: '🏪', color: 'bg-purple-500 hover:bg-purple-600' },
                      { value: 'ENTRADA_PAGA_EM_LOJA', label: 'Entrada Paga em Loja', icon: '💳', color: 'bg-orange-500 hover:bg-orange-600' }
                    ].map((tipo) => (
                      <button
                        key={tipo.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, tipoNegociacao: tipo.value })}
                        className={`p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center justify-center space-y-1 text-xs font-medium ${
                          formData.tipoNegociacao === tipo.value
                            ? `${tipo.color} text-white border-transparent shadow-md`
                            : 'bg-card text-foreground border-gray-300 hover:border-gray-400 hover:bg-muted/50'
                        }`}
                      >
                        <span className="text-sm">{tipo.icon}</span>
                        <span className="text-center leading-tight">{tipo.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Seção de Upload de Comprovantes */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Comprovantes de Pagamento</label>
                  
                  {/* Área de Upload */}
                  <div className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-4 text-center hover:border-primary transition-colors">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-muted-foreground">
                        Clique para selecionar arquivos ou arraste aqui
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        PDF, JPG, PNG, DOC (máx. 10MB cada)
                      </p>
                    </label>
                  </div>

                  {/* Lista de arquivos selecionados */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <h4 className="text-sm font-medium text-foreground">Arquivos selecionados:</h4>
                      <div className="grid grid-cols-1 gap-3">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="bg-muted/50 border border-border rounded-lg p-3">
                            {/* Preview do documento */}
                            <div className="mb-3">
                              <div className="w-full h-32 bg-muted rounded-lg flex items-center justify-center border border-border">
                                <div className="text-center">
                                  <div className="text-3xl mb-2">{getFileIcon(file.name)}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {file.name.split('.').pop()?.toUpperCase() || 'ARQUIVO'}
                                  </div>
                                  <div className="text-xs text-muted-foreground">{file.name}</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Informações do arquivo */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <span className="text-lg">{getFileIcon(file.name)}</span>
                                <div>
                                  <p className="text-sm font-medium">{file.name}</p>
                                  <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeSelectedFile(index)}
                                className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button
                        type="button"
                        onClick={uploadFiles}
                        disabled={uploadingFiles}
                        className="w-full mt-2"
                        variant="outline"
                      >
                        {uploadingFiles ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Enviar Comprovantes
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Lista de arquivos já enviados */}
                  {uploadedFiles.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <h4 className="text-sm font-medium text-foreground">Comprovantes enviados:</h4>
                      <div className="grid grid-cols-1 gap-3">
                        {uploadedFiles.map((file) => (
                          <div key={file.id} className="bg-green-50 border border-green-200 rounded-lg p-3">
                            {/* Preview do documento */}
                            <div className="mb-3">
                              {getFilePreview(file)}
                            </div>
                            
                            {/* Informações do arquivo */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <span className="text-lg">{getFileIcon(file.fileName)}</span>
                                <div>
                                  <p className="text-sm font-medium">{file.fileName}</p>
                                  <p className="text-xs text-muted-foreground">{formatFileSize(file.fileSize)}</p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <button
                                  type="button"
                                  onClick={() => openFileViewer(file.url, file.fileName)}
                                  className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-950/40 dark:hover:bg-blue-950/40 dark:hover:bg-blue-950/40"
                                  title="Visualizar"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeUploadedFile(file.id)}
                                  className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                  title="Remover"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              <Button type="submit" className="w-full">
                  Registrar Contato
                </Button>
              </form>
            </div>

            {/* Timeline de Feedbacks */}
            <div className="flex flex-col h-full">
              <h3 className="text-lg font-semibold mb-4 text-foreground">Histórico de Contatos</h3>
              <div className="flex-1 overflow-hidden">
                {loadingFeedbacks ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : safeArray(feedbacks).length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mb-4 opacity-50" />
                    <p className="text-sm">Nenhum contato registrado ainda</p>
                  </div>
                ) : (
                                    <div className="space-y-4 h-full overflow-y-auto pr-2">
                    {safeArray(feedbacks).map((feedback) => (
                      <div key={feedback.id} className="relative">
                        {/* Linha conectora */}
                        <div className="absolute left-6 top-8 bottom-0 w-0.5 bg-gray-200 dark:bg-slate-600" />
                        
                        <div className="flex items-start space-x-4">
                          {/* Ícone */}
                          <div className={`flex-shrink-0 w-12 h-12 rounded-full ${getFeedbackColor(feedback.tipo)} flex items-center justify-center relative`}>
                            {getFeedbackIcon(feedback.tipo)}
                            {feedback.audio && (
                              <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                <Volume2 className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>

                          {/* Conteúdo */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium">
                                  {feedback.user?.name || 'Usuário'}
                                </span>
                                <span className={`text-xs px-2 py-1 rounded-full ${getFeedbackColor(feedback.tipo)}`}>
                                  {feedback.tipo}
                                </span>
                                
                                {feedback.audio && (
                                  <span className="text-xs text-blue-600 flex items-center space-x-1">
                                    <Volume2 className="w-3 h-3" />
                                    <span>Áudio</span>
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {formatDateTime(feedback.createdAt)}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => handleOpenPayment(feedback)}
                                >
                                  Ação do gestor
                                </Button>
                              </div>
                            </div>

                            <div className="text-sm text-foreground mb-2">
                              {feedback.descricao}
                            </div>

                            {/* Próximo Contato se aplicável */}
                            {feedback.proximoContato && (
                              <div className="mb-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  Próximo contato: {formatDateTime(feedback.proximoContato)}
                                </span>
                              </div>
                            )}

                            {/* Tipo de Negociação se aplicável */}
                            {feedback.tipoNegociacao && (
                              <div className="mb-2 flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <DollarSign className="w-3 h-3 mr-1" />
                                  {formatTipoNegociacao(feedback.tipoNegociacao)}
                                  {feedback.valorNegociado && (
                                    <span className="ml-1 font-semibold">
                                      - {formatCurrency(parseFloat(feedback.valorNegociado))}
                                    </span>
                                  )}
                                </span>
                                {feedback.titulo?.company?.name && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-foreground border">
                                    {feedback.titulo.company.name}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Debug: Mostrar valor negociado mesmo sem tipo de negociação */}
                            {!feedback.tipoNegociacao && feedback.valorNegociado && (
                              <div className="mb-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <DollarSign className="w-3 h-3 mr-1" />
                                  Valor: {formatCurrency(parseFloat(feedback.valorNegociado))}
                                </span>
                              </div>
                            )}

                            {/* Informações do título se aplicável */}
                            {feedback.titulo && (
                              <div className="bg-muted/50 rounded-lg p-3 text-xs">
                                <div className="text-foreground">
                                  <div className="font-medium">Título: {feedback.titulo.descricaoTitulo || feedback.titulo.numero}</div>
                                  {feedback.titulo.observacoes && (
                                    <div className="mt-1 text-muted-foreground">
                                      Observações: {feedback.titulo.numero} - {feedback.titulo.observacoes}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Títulos negociados/marcados */}
                            {(() => {
                              console.log('Renderizando feedback:', {
                                id: feedback.id,
                                titulosNegociados: feedback.titulosNegociados,
                                hasTitulos: !!feedback.titulosNegociados,
                                length: feedback.titulosNegociados?.length || 0
                              });
                              return feedback.titulosNegociados && feedback.titulosNegociados.length > 0;
                            })() && (
                              <div className="bg-blue-50 dark:bg-blue-950/40 rounded-lg p-3 text-xs">
                                <div className="text-blue-700 mb-2">
                                  <div className="font-medium flex items-center">
                                    <FileText className="w-3 h-3 mr-1" />
                                    Títulos relacionados ({feedback.titulosNegociados.length})
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  {feedback.titulosNegociados.map((titulo: any) => (
                                    <div key={titulo.id} className="bg-card rounded p-2 border border-blue-200">
                                      <div className="font-medium text-blue-800">
                                        {titulo.numero} - {titulo.company?.name || 'Empresa'}
                                      </div>
                                      <div className="text-blue-600 text-xs mt-1">
                                        <span className="mr-2">Venc: {formatDate(titulo.vencimento)}</span>
                                        <span className="mr-2">R$ {formatCurrency(titulo.valor)}</span>
                                        <span className={`px-1 py-0.5 rounded text-xs ${
                                          effectiveTituloStatus(titulo.status, titulo.vencimento) === 'VENCIDO'
                                            ? 'bg-red-100 text-red-700'
                                            :
                                          titulo.status === 'PAGO' ? 'bg-green-100 text-green-700' :
                                          'bg-gray-100 text-foreground'
                                        }`}>
                                          {effectiveTituloStatus(titulo.status, titulo.vencimento)}
                                        </span>
                                      </div>
                                      {titulo.observacoes && (
                                        <div className="text-blue-600 text-xs mt-1 italic">
                                          Obs: {titulo.observacoes}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Áudio anexado se aplicável */}
                            {feedback.audio && (
                              <div className="mt-3">
                                <AudioPlayer 
                                  audioUrl={feedback.audio.url}
                                  transcript={feedback.audio.transcript}
                                />
                              </div>
                            )}

                            {/* Arquivos anexados se aplicável */}
                            {feedback.comprovantes && feedback.comprovantes.length > 0 && (
                              <div className="mt-3">
                                <div className="text-xs font-medium text-foreground mb-2 flex items-center">
                                  <FileText className="w-3 h-3 mr-1" />
                                  Arquivos Anexados ({feedback.comprovantes.length})
                                </div>
                                <div className="space-y-2">
                                  {feedback.comprovantes.map((comprovante: any) => (
                                    <div key={comprovante.id} className="flex items-center justify-between bg-muted/50 rounded-lg p-2 border border-border">
                                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                                        <span className="text-lg">{getFileIcon(comprovante.fileName)}</span>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-medium text-foreground truncate">
                                            {comprovante.originalName}
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                            {formatFileSize(comprovante.fileSize)}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <button
                                          type="button"
                                          onClick={() => openFileViewer(comprovante.url, comprovante.fileName)}
                                          className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-950/40 dark:hover:bg-blue-950/40 dark:hover:bg-blue-950/40"
                                          title="Visualizar"
                                        >
                                          <Eye className="w-3 h-3" />
                                        </button>
                                        {user?.role === 'ADMIN' && (
                                          <button
                                            type="button"
                                            onClick={() => removeFeedbackFile(comprovante.id)}
                                            className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                            title="Remover arquivo"
                                          >
                                            <X className="w-3 h-3" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
              </div>
            </div>
          </div>
        )}

        {/* Aba de Títulos */}
        {activeTab === 'titulos' && (
          <div className="space-y-6">
            {(() => {
              const titulos = safeArray(selectedCard?.titulos);
              const titulosAgrupados = groupTitulosByStatus(titulos);
              
              // Filtrar apenas títulos vencidos e parcialmente pagos
              const titulosVencidos = titulosAgrupados['VENCIDO'] || [];
              const titulosParciais = titulosAgrupados['PARCIAL'] || [];
              
              const valorTotalVencidos = titulosVencidos.reduce((sum: number, titulo: any) => 
                sum + Number(titulo.saldo || titulo.valor), 0
              );
              const valorTotalParciais = titulosParciais.reduce((sum: number, titulo: any) => 
                sum + Number(titulo.saldo || titulo.valor), 0
              );
              
              return (
                <div className="space-y-4">
                  {/* Abas de Títulos */}
                  <div className="border border-border rounded-lg">
                    <div className="flex border-b border-border">
                      {/* Aba Vencidos */}
                      <button
                        onClick={() => setTitulosActiveTab('vencidos')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                          titulosActiveTab === 'vencidos'
                            ? 'bg-red-50 text-red-700 border-b-2 border-red-500'
                            : 'bg-gray-50 text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Títulos Vencidos</span>
                          <Badge variant="outline" className="text-xs">
                            {titulosVencidos.length}
                          </Badge>
                        </div>
                      </button>
                      
                      {/* Aba Parciais */}
                      <button
                        onClick={() => setTitulosActiveTab('parciais')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                          titulosActiveTab === 'parciais'
                            ? 'bg-orange-50 text-orange-700 border-b-2 border-orange-500'
                            : 'bg-gray-50 text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <Clock className="w-4 h-4" />
                          <span>Pagamento Parcial</span>
                          <Badge variant="outline" className="text-xs">
                            {titulosParciais.length}
                          </Badge>
                        </div>
                      </button>
                    </div>

                    {/* Conteúdo das Abas */}
                    <div className="p-4">

                      {/* Aba Vencidos */}
                      {titulosActiveTab === 'vencidos' && (
                        <div>
                          {/* Header com valor total */}
                          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <AlertTriangle className="w-5 h-5 text-red-600" />
                                <span className="font-medium text-red-800">Valor Total Vencido</span>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold text-red-800">
                                  {formatCurrency(valorTotalVencidos)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Lista de títulos vencidos */}
                          {titulosVencidos.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                              <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                              <p className="text-sm">Nenhum título vencido</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {titulosVencidos.map((titulo: any) => (
                                <div key={titulo.id} className="p-3 bg-card border border-border rounded-lg hover:bg-muted/50">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-3">
                                      <div className="flex-shrink-0">
                                        <Building2 className="w-4 h-4 text-muted-foreground" />
                                      </div>
                                      <div>
                                        <div className="font-medium text-sm">
                                          ID: {titulo.numero}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          Vencimento: {formatDate(titulo.vencimento)}
                                        </div>
                                        <div className="text-xs text-blue-600 font-medium">
                                          {titulo.company?.name || 'Empresa não informada'}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                          <div>Total: {formatCurrency(titulo.valor)}</div>
                                          {titulo.valorPago && titulo.valorPago > 0 && (
                                            <div className="text-green-600">Pago: {formatCurrency(titulo.valorPago)}</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className="text-right">
                                      <div className="font-semibold text-sm text-red-600">
                                        {formatCurrency(titulo.saldo || titulo.valor)}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        Saldo: {formatCurrency(titulo.saldo || titulo.valor)}
                                      </div>
                                      <div className="text-xs text-red-600 font-medium">
                                        {effectiveTituloStatus(titulo.status, titulo.vencimento)}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Informações do Título - Layout em Duas Colunas */}
                                  <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    {/* Coluna 1: Link do Boleto */}
                                    {titulo.urlBoleto && (
                                      <div className="p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded text-xs">
                                        <div className="flex items-center space-x-1 mb-2">
                                          <ExternalLink className="h-3 w-3 text-blue-600" />
                                          <span className="font-medium text-blue-700">Link do Boleto</span>
                                        </div>
                                        <p className="text-blue-600 break-all mb-2 text-xs">
                                          {titulo.urlBoleto}
                                        </p>
                                        <div className="flex space-x-1">
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              copyToClipboard(titulo.urlBoleto);
                                            }}
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs"
                                          >
                                            <Copy className="h-3 w-3 mr-1" />
                                            Copiar
                                          </Button>
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(titulo.urlBoleto, '_blank');
                                            }}
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs"
                                          >
                                            <ExternalLink className="h-3 w-3 mr-1" />
                                            Abrir
                                          </Button>
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              sendToWhatsApp(titulo.urlBoleto, titulo.numero);
                                            }}
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 text-green-700 hover:bg-green-100"
                                          >
                                            <Send className="h-3 w-3 mr-1" />
                                            WhatsApp
                                          </Button>
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              sendToEmail(titulo.urlBoleto, titulo.numero);
                                            }}
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 dark:hover:bg-blue-900/50"
                                          >
                                            <Mail className="h-3 w-3 mr-1" />
                                            E-mail
                                          </Button>
                                        </div>
                                      </div>
                                    )}

                                    {/* Coluna 2: Observações */}
                                    <div className="p-3 bg-muted/50 border border-border rounded text-xs">
                                      <div className="flex items-center space-x-1 mb-2">
                                        <FileText className="h-3 w-3 text-muted-foreground" />
                                        <span className="font-medium text-foreground">Observações</span>
                                      </div>
                                      <div className="text-muted-foreground text-xs">
                                        {titulo.observacoes ? (
                                          <p className="break-words">{titulo.observacoes}</p>
                                        ) : (
                                          <p className="text-gray-400 italic">Nenhuma observação registrada</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Aba Parciais */}
                      {titulosActiveTab === 'parciais' && (
                        <div>
                          {/* Header com valor total */}
                          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <Clock className="w-5 h-5 text-orange-600" />
                                <span className="font-medium text-orange-800">Valor Total em Saldo</span>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold text-orange-800">
                                  {formatCurrency(valorTotalParciais)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Lista de títulos parciais */}
                          {titulosParciais.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                              <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                              <p className="text-sm">Nenhum título com pagamento parcial</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {titulosParciais.map((titulo: any) => (
                                <div key={titulo.id} className="p-3 bg-card border border-border rounded-lg hover:bg-muted/50">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-3">
                                      <div className="flex-shrink-0">
                                        <Building2 className="w-4 h-4 text-muted-foreground" />
                                      </div>
                                      <div>
                                        <div className="font-medium text-sm">
                                          ID: {titulo.numero}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          Vencimento: {formatDate(titulo.vencimento)}
                                        </div>
                                        <div className="text-xs text-blue-600 font-medium">
                                          {titulo.company?.name || 'Empresa não informada'}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                          <div>Total: {formatCurrency(titulo.valor)}</div>
                                          {titulo.valorPago && titulo.valorPago > 0 && (
                                            <div className="text-green-600">Pago: {formatCurrency(titulo.valorPago)}</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className="text-right">
                                      <div className="font-semibold text-sm text-orange-600">
                                        {formatCurrency(titulo.saldo || titulo.valor)}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        Saldo: {formatCurrency(titulo.saldo || titulo.valor)}
                                      </div>
                                      <div className="text-xs text-orange-600 font-medium">
                                        {effectiveTituloStatus(titulo.status, titulo.vencimento)}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Informações do Título - Layout em Duas Colunas */}
                                  <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    {/* Coluna 1: Link do Boleto */}
                                    {titulo.urlBoleto && (
                                      <div className="p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded text-xs">
                                        <div className="flex items-center space-x-1 mb-2">
                                          <ExternalLink className="h-3 w-3 text-blue-600" />
                                          <span className="font-medium text-blue-700">Link do Boleto</span>
                                        </div>
                                        <p className="text-blue-600 break-all mb-2 text-xs">
                                          {titulo.urlBoleto}
                                        </p>
                                        <div className="flex space-x-1">
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              copyToClipboard(titulo.urlBoleto);
                                            }}
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs"
                                          >
                                            <Copy className="h-3 w-3 mr-1" />
                                            Copiar
                                          </Button>
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(titulo.urlBoleto, '_blank');
                                            }}
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs"
                                          >
                                            <ExternalLink className="h-3 w-3 mr-1" />
                                            Abrir
                                          </Button>
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              sendToWhatsApp(titulo.urlBoleto, titulo.numero);
                                            }}
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 text-green-700 hover:bg-green-100"
                                          >
                                            <Send className="h-3 w-3 mr-1" />
                                            WhatsApp
                                          </Button>
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              sendToEmail(titulo.urlBoleto, titulo.numero);
                                            }}
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 dark:hover:bg-blue-900/50"
                                          >
                                            <Mail className="h-3 w-3 mr-1" />
                                            E-mail
                                          </Button>
                                        </div>
                                      </div>
                                    )}

                                    {/* Coluna 2: Observações */}
                                    <div className="p-3 bg-muted/50 border border-border rounded text-xs">
                                      <div className="flex items-center space-x-1 mb-2">
                                        <FileText className="h-3 w-3 text-muted-foreground" />
                                        <span className="font-medium text-foreground">Observações</span>
                                      </div>
                                      <div className="text-muted-foreground text-xs">
                                        {titulo.observacoes ? (
                                          <p className="break-words">{titulo.observacoes}</p>
                                        ) : (
                                          <p className="text-gray-400 italic">Nenhuma observação registrada</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {titulos.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">Nenhum título encontrado</p>
                      <p className="text-sm">Este cliente/fornecedor não possui títulos cadastrados.</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Aba de Mudança de Etapa */}
        {activeTab === 'mudar-etapa' && (
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
             
            </div>

            {/* Etapa Atual */}
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <h4 className="text-sm font-medium text-foreground mb-2">Etapa Atual</h4>
              <div className="flex items-center space-x-3">
                <div 
                  className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: (etapas.find((e: any) => e.id === findCardEtapa(selectedCard?.id)) as any)?.cor || '#ccc' }}
                />
                <span className="text-sm font-medium">
                  {(etapas.find((e: any) => e.id === findCardEtapa(selectedCard?.id)) as any)?.nome || 'Etapa não encontrada'}
                </span>
              </div>
            </div>

            {/* Seleção da Nova Etapa */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-foreground">Nova Etapa</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {etapas.map((etapa: any) => {
                  const isCurrentEtapa = etapa.id === findCardEtapa(selectedCard?.id);
                  const isSelected = selectedEtapaId === etapa.id;
                  
                  return (
                    <div
                      key={etapa.id}
                      onClick={() => !isCurrentEtapa && setSelectedEtapaId(etapa.id)}
                      className={`
                        p-4 border rounded-lg cursor-pointer transition-all
                        ${isCurrentEtapa 
                          ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-60' 
                          : isSelected 
                            ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' 
                            : 'bg-card border-gray-200 hover:bg-muted/50 hover:border-gray-300 dark:border-slate-600'
                        }
                      `}
                    >
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                          style={{ backgroundColor: etapa.cor }}
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{etapa.nome}</div>
                          {etapa.descricao && (
                            <div className="text-xs text-muted-foreground mt-1">{etapa.descricao}</div>
                          )}
                        </div>
                        {isCurrentEtapa && (
                          <Badge variant="secondary" className="text-xs">
                            Atual
                          </Badge>
                        )}
                        {isSelected && !isCurrentEtapa && (
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Botão de Confirmação */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedEtapaId('');
                  setActiveTab('feedback');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleChangeEtapa}
                disabled={!selectedEtapaId || isChangingEtapa || selectedEtapaId === findCardEtapa(selectedCard?.id)}
                className="flex items-center space-x-2"
              >
                {isChangingEtapa ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Alterando...</span>
                  </>
                ) : (
                  <>
                    <Move className="w-4 h-4" />
                    <span>Mudar Etapa</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Aba de WhatsApp Chat */}
        {activeTab === 'whatsapp' && (
          <div className="space-y-4">
            {!selectedCard?.cliente?.id ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Selecione um cliente para iniciar o chat</p>
                </div>
              </div>
            ) : (
              <>
                {/* Barra de Ferramentas do Chat - Melhorada */}
                <div className="flex items-center justify-between p-3 bg-card rounded-xl border-2 border-border shadow-sm">
                  <div className="flex items-center gap-2">
                    {/* Busca */}
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar mensagens..."
                        className="pl-8 w-48 text-sm"
                      />
                    </div>
                    
                    {/* Botão Templates Rápidos */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowQuickReplies(!showQuickReplies)}
                    >
                      <Smile className="w-4 h-4 mr-1" />
                      Templates
                    </Button>
                  </div>
                  
                  {/* Controles */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSoundEnabled(!soundEnabled)}
                      title={soundEnabled ? 'Desativar som' : 'Ativar som'}
                    >
                      {soundEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => refetchChatMessages()}
                      title="Atualizar mensagens"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Templates Rápidos - Melhorado */}
                {showQuickReplies && whatsappTemplates.length > 0 && (
                  <div className="bg-card border-2 border-blue-200 dark:border-blue-700 rounded-xl p-4 max-h-64 overflow-y-auto shadow-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground">Templates Rápidos</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowQuickReplies(false)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {whatsappTemplates.slice(0, 6).map((template: any) => (
                        <Button
                          key={template.id}
                          variant="outline"
                          size="sm"
                          className="text-left justify-start h-auto py-3 px-4 text-sm hover:bg-blue-50 dark:hover:bg-blue-950/40 dark:hover:bg-blue-950/40 dark:hover:bg-blue-900/20 hover:border-blue-400 dark:hover:border-blue-500 transition-all"
                          onClick={() => {
                            // Processar variáveis básicas
                            let content = template.content || '';
                            const cliente = selectedCard?.cliente;
                            if (cliente) {
                              content = content.replace(/\{nome\}/g, cliente.nome || 'Cliente');
                              const titulos = safeArray(selectedCard?.titulos || []);
                              const titulosVencidos = titulos.filter(
                                (t: any) =>
                                  effectiveTituloStatus(t.status, t.vencimento) === 'VENCIDO' ||
                                  t.status === 'PARCIAL'
                              );
                              const valorTotal = titulosVencidos.reduce((sum: number, t: any) => sum + Number(t.valor || 0), 0);
                              content = content.replace(/\{valor\}/g, formatCurrency(valorTotal));
                              content = content.replace(/\{titulos\}/g, titulosVencidos.length.toString());
                            }
                            setChatMessage(content);
                            setShowQuickReplies(false);
                          }}
                        >
                          <span className="truncate">{template.name}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Lista de Mensagens - Melhorada */}
                <div className="border border-border rounded-xl h-[500px] overflow-y-auto bg-gradient-to-b from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 p-6 space-y-4 relative shadow-inner">
                  {filteredMessages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {searchQuery ? 'Nenhuma mensagem encontrada' : 'Nenhuma mensagem ainda'}
                        </p>
                        {!searchQuery && (
                          <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Inicie a conversa enviando uma mensagem</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      {filteredMessages.map((msg: any, index: number) => {
                        const msgDate = new Date(msg.timestamp);
                        const prevMsg = index > 0 ? filteredMessages[index - 1] : null;
                        const prevMsgDate = prevMsg ? new Date(prevMsg.timestamp) : null;
                        const showDateSeparator = !prevMsgDate || !isSameDay(msgDate, prevMsgDate);
                        
                        return (
                          <React.Fragment key={msg.id}>
                            {showDateSeparator && (
                              <div className="flex items-center justify-center my-4">
                                <div className="bg-gray-200 dark:bg-slate-700 text-muted-foreground text-xs px-3 py-1 rounded-full">
                                  {msgDate.toLocaleDateString('pt-BR', { 
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                  })}
                                </div>
                              </div>
                            )}
                            <div
                              className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'} group`}
                            >
                          <div
                            className={`max-w-[75%] rounded-2xl p-4 relative shadow-md transition-all hover:shadow-lg ${
                              msg.direction === 'outbound'
                                ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-tr-sm'
                                : 'bg-card text-gray-900 dark:text-slate-200 border border-border rounded-tl-sm'
                            }`}
                          >
                            {/* Mensagem sendo respondida */}
                            {replyingTo?.id === msg.id && (
                              <div className={`mb-2 p-2 rounded text-xs border-l-2 ${
                                msg.direction === 'outbound'
                                  ? 'bg-blue-400 text-white border-blue-300'
                                  : 'bg-muted text-muted-foreground border-gray-300'
                              }`}>
                                <div className="font-medium">Respondendo a {msg.direction === 'outbound' ? 'você' : selectedCard?.cliente?.nome}</div>
                                <div className="truncate">{msg.content?.substring(0, 50)}...</div>
                              </div>
                            )}

                            {/* Conteúdo da mensagem com formatação */}
                            <div 
                              className="text-sm whitespace-pre-wrap break-words"
                              dangerouslySetInnerHTML={{ 
                                __html: formatMessageContent(msg.content || '') 
                              }}
                            />

                            {/* Mídia se houver */}
                            {msg.mediaUrl && (
                              <div className="mt-2">
                                {msg.messageType === 'image' ? (
                                  <img 
                                    src={msg.mediaUrl} 
                                    alt="Mídia" 
                                    className="max-w-full rounded-lg"
                                    onClick={() => window.open(msg.mediaUrl, '_blank')}
                                  />
                                ) : (
                                  <a 
                                    href={msg.mediaUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-blue-400 hover:text-blue-300 underline"
                                  >
                                    <Paperclip className="w-4 h-4" />
                                    <span>Arquivo anexado</span>
                                  </a>
                                )}
                              </div>
                            )}

                            {/* Footer da mensagem */}
                            <div className={`flex items-center justify-between mt-2 text-xs gap-2 ${
                              msg.direction === 'outbound' ? 'text-blue-100' : 'text-muted-foreground'
                            }`}>
                              <div className="flex items-center gap-1">
                                <span>
                                  {msg.direction === 'outbound' ? 'Você' : selectedCard?.cliente?.nome || 'Cliente'}
                                </span>
                                <span>•</span>
                                <span>
                                  {new Date(msg.timestamp).toLocaleTimeString('pt-BR', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </span>
                              </div>
                              
                              {/* Status da mensagem (apenas para mensagens enviadas) */}
                              {msg.status && msg.direction === 'outbound' && (
                                <div className="flex items-center gap-1" title={getStatusText(msg.status)}>
                                  {getStatusIcon(msg.status)}
                                </div>
                              )}
                            </div>

                            {/* Botão de resposta (hover) */}
                            {msg.direction === 'inbound' && (
                              <button
                                onClick={() => setReplyingTo(msg)}
                                className="absolute -left-8 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-card rounded-full shadow-md hover:bg-gray-50 dark:hover:bg-slate-600"
                                title="Responder"
                              >
                                <Reply className="w-3 h-3 text-muted-foreground" />
                              </button>
                            )}
                          </div>
                        </div>
                        </React.Fragment>
                      );
                      })}
                      
                      {/* Indicador de digitação - Melhorado */}
                      {isTyping && (
                        <div className="flex justify-start">
                          <div className="bg-card border-2 border-border rounded-2xl rounded-tl-sm p-4 shadow-md">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground font-medium">
                                {selectedCard?.cliente?.nome || 'Cliente'} está digitando
                              </span>
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div ref={chatMessagesEndRef} />
                    </>
                  )}
                </div>

                {/* Preview de mídia selecionada */}
                {mediaPreview && (
                  <div className="bg-card border-2 border-blue-200 dark:border-blue-700 rounded-xl p-4 shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" />
                        Preview da Mídia
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedMedia(null);
                          setMediaPreview(null);
                        }}
                        className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    {selectedMedia?.type.startsWith('image/') ? (
                      <div className="relative rounded-lg overflow-hidden border border-border">
                        <img src={mediaPreview} alt="Preview" className="max-w-full max-h-48 object-contain mx-auto" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                        <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                          <Paperclip className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-slate-200 truncate">{selectedMedia?.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(selectedMedia?.size || 0) / 1024 > 1024 
                              ? `${((selectedMedia?.size || 0) / 1024 / 1024).toFixed(2)} MB`
                              : `${((selectedMedia?.size || 0) / 1024).toFixed(2)} KB`}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Área de Input de Mensagem - Melhorada */}
                <div className="space-y-3 bg-card rounded-xl border-2 border-border p-4 shadow-lg">
                  {/* Mensagem sendo respondida */}
                  {replyingTo && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 dark:border-blue-400 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          <Reply className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-1">
                            Respondendo a {selectedCard?.cliente?.nome}
                          </div>
                          <div className="text-xs text-blue-600 dark:text-blue-400 truncate bg-card px-2 py-1 rounded">
                            {replyingTo.content?.substring(0, 60)}{replyingTo.content && replyingTo.content.length > 60 ? '...' : ''}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setReplyingTo(null)}
                        className="h-8 w-8 p-0 flex-shrink-0 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  )}

                  {/* Área de Input Principal */}
                  <div className="flex items-end gap-3">
                    {/* Botão de anexo */}
                    <label className="flex items-center justify-center h-12 w-12 border-2 border-gray-300 dark:border-slate-600 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer transition-all hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md flex-shrink-0">
                      <input
                        type="file"
                        accept="image/*,application/pdf,.doc,.docx,audio/*,video/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setSelectedMedia(file);
                            if (file.type.startsWith('image/')) {
                              const reader = new FileReader();
                              reader.onload = (e) => {
                                setMediaPreview(e.target?.result as string);
                              };
                              reader.readAsDataURL(file);
                            } else {
                              setMediaPreview('file');
                            }
                          }
                        }}
                      />
                      <Paperclip className="w-5 h-5 text-muted-foreground" />
                    </label>

                    {/* Textarea para mensagem */}
                    <div className="flex-1 relative">
                      <Textarea
                        value={chatMessage}
                        onChange={(e) => {
                          setChatMessage(e.target.value);
                          handleTyping();
                          // Auto-resize
                          e.target.style.height = 'auto';
                          e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendChatMessage();
                          }
                        }}
                        placeholder="Digite sua mensagem... (Shift+Enter para nova linha)"
                        className="min-h-[48px] max-h-[120px] resize-none pr-12 py-3 text-sm border-2 border-gray-300 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400 rounded-xl shadow-sm"
                        rows={1}
                      />
                      {/* Contador de caracteres (opcional) */}
                      {chatMessage.length > 0 && (
                        <div className="absolute bottom-2 right-3 text-xs text-gray-400 dark:text-slate-500">
                          {chatMessage.length}
                        </div>
                      )}
                    </div>

                    {/* Botão de enviar */}
                    <Button
                      onClick={handleSendChatMessage}
                      disabled={(!chatMessage.trim() && !selectedMedia) || isSendingChatMessage}
                      className="h-12 w-12 p-0 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      {isSendingChatMessage ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>

                  {/* Dica de atalho */}
                  <div className="text-xs text-muted-foreground flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Enter</kbd>
                      <span>para enviar</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Shift</kbd>
                      <span>+</span>
                      <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Enter</kbd>
                      <span>para nova linha</span>
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Aba Time-line do status */}
        {activeTab === 'timeline' && (
          <div className="space-y-4">
            {(() => {
              const titulos = safeArray(selectedCard?.titulos);
              if (titulos.length === 0) {
                return (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Nenhum título encontrado</p>
                    </div>
                  </div>
                );
              }

              return (
                <div className="space-y-6">
                  {titulos.map((titulo: any) => (
                    <TimelineCard key={titulo.id} tituloId={titulo.id} tituloNumero={titulo.numero} />
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Aba de Transferência de Usuário */}
        {activeTab === 'transferir-usuario' && (user?.role === 'ADMIN' || user?.role === 'GESTOR') && (
          <div className="space-y-6">
            {/* Usuários Disponíveis */}
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <h4 className="text-sm font-medium text-foreground mb-2">Operadores Disponíveis</h4>
              {loadingUsers ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm">Nenhum operador disponível para transferência.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-card border border-border rounded-lg hover:bg-muted/50 cursor-pointer"
                      onClick={() => setSelectedUserId(user.id)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">{user.name}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      {selectedUserId === user.id && (
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Botão de Confirmação */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedUserId('');
                  setActiveTab('feedback');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleTransferUser}
                disabled={!selectedUserId || isTransferringUser}
                className="flex items-center space-x-2"
              >
                {isTransferringUser ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Transferindo...</span>
                  </>
                ) : (
                  <>
                    <Users className="w-4 h-4" />
                    <span>Transferir Usuário</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Aba Transferir Kanban */}
        {activeTab === 'transferir-kanban' && (
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start space-x-2">
                <ArrowRight className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-100">
                    Transferir Card para Outro Kanban
                  </h4>
                  <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                    Selecione um kanban de destino para transferir todos os títulos deste cliente.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center py-8">
              <Button
                onClick={() => {
                  console.log('🔍 Abrindo modal de transferência de kanban');
                  console.log('📋 selectedCard:', selectedCard);
                  console.log('🎯 selectedKanbanId:', selectedKanbanId);
                  console.log('📊 selectedKanban:', selectedKanban);
                  setIsTransferKanbanModalOpen(true);
                }}
                className="flex items-center space-x-2"
              >
                <ArrowRight className="w-4 h-4" />
                <span>Selecionar Kanban de Destino</span>
              </Button>
            </div>
          </div>
        )}

      </KanbanCardModalLazy>

      {paymentModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-slate-900/80">
          <div className="bg-card rounded-lg shadow-lg dark:shadow-2xl dark:shadow-slate-900/50 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-border">
                              <h3 className="text-lg font-semibold text-foreground">Ação do gestor</h3>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="space-y-4">
                {/* Seleção de títulos (apenas do feedback) */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Títulos do feedback original</label>
                                      <div className="max-h-32 overflow-auto border border-border rounded p-2 space-y-2 bg-card">
                    {(() => {
                      // Buscar feedback original para pegar os títulos marcados
                      const feedbackOriginal = feedbacks.find((f: any) => f.id === paymentFeedbackId);
                      const titulosDoFeedback = feedbackOriginal?.titulosNegociados || [];
                      
                      if (titulosDoFeedback.length === 0) {
                        return <p className="text-sm text-muted-foreground italic">Nenhum título encontrado no feedback original</p>;
                      }
                      
                      return titulosDoFeedback.map((t: any) => {
                        const checked = paymentTitulosSelecionados.includes(t.id);
                        return (
                          <label key={t.id} className="flex items-center justify-between text-sm gap-3">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...paymentTitulosSelecionados, t.id]
                                    : paymentTitulosSelecionados.filter((id) => id !== t.id);
                                  setPaymentTitulosSelecionados(next);
                                }}
                                className="rounded"
                              />
                              <span className="text-gray-700 dark:text-gray-300">
                                {t.numero} - {t.company?.name || 'Empresa'} - Venc: {formatDate(t.vencimento)}
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground">{formatCurrency(Number(t.saldo || t.valor || 0))}</span>
                          </label>
                        );
                      });
                    })()}
                  </div>
                  {paymentTitulosSelecionados.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{paymentTitulosSelecionados.length} título(s) selecionado(s)</p>
                  )}
                </div>

                {/* Resumo e ações rápidas */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="p-3 bg-muted/50 border border-border rounded">
                    {(() => {
                      const saldo = getSaldoSelecionado();
                      const valor = parseCurrency(paymentValorNeg);
                      const diff = Number((valor - saldo).toFixed(2));
                      const perc = saldo > 0 ? Number(((valor / saldo) * 100 - 100).toFixed(2)) : 0;
                      const color = diff === 0 ? 'text-blue-700' : diff < 0 ? 'text-green-700' : 'text-orange-700';
                      const bg = diff === 0 ? 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800' : diff < 0 ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800' : 'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800';
                      return (
                        <div className={`p-3 rounded border ${bg}`}>
                          <div className="text-sm text-foreground">Saldo selecionado</div>
                          <div className="text-lg font-bold">{formatCurrency(saldo)}</div>
                          <div className="mt-2 text-sm text-foreground">Valor negociado</div>
                          <div className="text-lg font-bold">{formatCurrency(valor)}</div>
                          <div className="mt-2 text-sm text-foreground">Diferença</div>
                          <div className={`text-lg font-bold ${color}`}>{formatCurrency(diff)} ({perc}%)</div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex flex-col gap-3">
                    <div>
                      <label className="text-sm font-medium text-foreground">Ações rápidas</label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <Button type="button" variant="outline" size="sm" onClick={() => applyQuickAction('igualSaldo')}>= saldo</Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => applyQuickAction('menos5')}>-5%</Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => applyQuickAction('menos10')}>-10%</Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => applyQuickAction('arredondar')}>Arredondar</Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => applyQuickAction('zerarCentavos')}>Zerar centavos</Button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-foreground">Estratégia de distribuição</label>
                      <div className="flex flex-col sm:flex-row gap-3 text-sm">
                        <label className="inline-flex items-center gap-2 text-foreground">
                          <input type="radio" name="dist" checked={paymentDistribuicao==='MAIS_ANTIGOS'} onChange={() => setPaymentDistribuicao('MAIS_ANTIGOS')} />
                          Mais antigos primeiro
                        </label>
                        <label className="inline-flex items-center gap-2 text-foreground">
                          <input type="radio" name="dist" checked={paymentDistribuicao==='PROPORCIONAL'} onChange={() => setPaymentDistribuicao('PROPORCIONAL')} />
                          Proporcional ao saldo
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Valores */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                                          <label className="block text-sm font-medium mb-1 text-foreground">Valor negociado</label>
                    <MaskedInput 
                      mask="currency" 
                      value={paymentValorNeg} 
                      onChange={(v) => setPaymentValorNeg(v)} 
                      onFocus={handleValorNegFocus}
                      onBlur={handleValorNegBlur}
                      placeholder="R$ 0,00" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-foreground">Valor pago (opcional)</label>
                    <MaskedInput 
                      mask="currency" 
                      value={paymentValorPago} 
                      onChange={(v) => setPaymentValorPago(v)} 
                      onFocus={handleValorPagoFocus}
                      onBlur={handleValorPagoBlur}
                      placeholder="R$ 0,00" 
                    />
                  </div>
                </div>

                {/* Data/Status */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-foreground">Data pagamento</label>
                    <Input type="date" value={paymentData} onChange={(e) => setPaymentData(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-foreground">Status do registro</label>
                    <select className="w-full border rounded px-2 py-2 text-sm" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value as any)}>
                      {['CONFIRMADO','PARCIAL','PENDENTE','CANCELADO','ESTORNADO'].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Observações */}
                <div>
                                      <label className="block text-sm font-medium mb-1 text-foreground">Observações</label>
                  <Textarea rows={3} value={paymentObs} onChange={(e) => setPaymentObs(e.target.value)} />
                </div>

                {/* Comprovante */}
                <div>
                                      <label className="block text-sm font-medium mb-1 text-foreground">Anexar comprovante (opcional)</label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input 
                        type="file" 
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            // Validar tipo de arquivo
                            const allowedTypes = [
                              'application/pdf',
                              'image/jpeg',
                              'image/jpg', 
                              'image/png',
                              'application/msword',
                              'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                            ];
                            
                            if (!allowedTypes.includes(file.type)) {
                              toast.error('Tipo de arquivo não permitido. Use PDF, JPG, PNG ou DOC.');
                              e.target.value = '';
                              setPaymentFile(null);
                              return;
                            }
                            
                            // Validar tamanho (10MB)
                            const maxSize = 10 * 1024 * 1024; // 10MB
                            if (file.size > maxSize) {
                              toast.error('Arquivo muito grande. Máximo 10MB.');
                              e.target.value = '';
                              setPaymentFile(null);
                              return;
                            }
                            
                            setPaymentFile(file);
                            toast.success('Arquivo selecionado com sucesso');
                          } else {
                            setPaymentFile(null);
                          }
                        }}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-950/40 file:text-blue-700 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/50"
                      />
                    </div>
                    
                    {paymentFile && (
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-green-600" />
                          <div>
                            <p className="text-sm font-medium text-green-800">{paymentFile.name}</p>
                            <p className="text-xs text-green-600">
                              {(paymentFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setPaymentFile(null);
                            // Limpar o input file
                            const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
                            if (fileInput) fileInput.value = '';
                          }}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      Tipos permitidos: PDF, JPG, PNG, DOC, DOCX. Máximo 10MB.
                    </p>
                  </div>
                </div>
              </div>
            </div>
                         <div className="p-4 border-t border-border flex justify-end gap-2 bg-gray-50 dark:bg-slate-700">
              <Button variant="outline" onClick={() => setPaymentModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSubmitPayment}>Salvar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer lateral de Estatísticas */}
      {user?.role === 'OPERADOR' && (
        <div className={`${isStatsOpen ? 'fixed' : 'hidden'} inset-0 z-50`}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 dark:bg-slate-900/80" onClick={() => setIsStatsOpen(false)} />
          {/* Painel lateral direito */}
          <div className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-card shadow-xl border-l border-border flex flex-col">
            <div className="p-4 border-b border-border flex items-center justify-between bg-gray-50 dark:bg-slate-700">
              <h3 className="font-semibold text-foreground">📊 Estatísticas do Operador</h3>
              <Button variant="ghost" size="sm" onClick={() => setIsStatsOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
                  <div className="p-4 overflow-y-auto space-y-4">
        {/* Cards de Performance Individual */}
        <div>
          <h4 className="font-semibold text-sm text-foreground mb-3">📊 Performance Individual</h4>
          <div className="grid grid-cols-2 gap-3">
            {/* Títulos Processados Hoje */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Processados Hoje</p>
                    <p className="text-xl font-bold">{isLoadingPerformance ? '...' : (performanceData?.data?.titulosProcessadosHoje || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Taxa de Conversão */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Taxa Conversão</p>
                    <p className="text-xl font-bold">{isLoadingPerformance ? '...' : `${performanceData?.data?.taxaConversao || 0}%`}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Tempo Médio */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Tempo Médio</p>
                    <p className="text-xl font-bold">{isLoadingPerformance ? '...' : `${performanceData?.data?.tempoMedioResolucao || 0}h`}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Ranking */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <Trophy className="w-4 h-4 text-yellow-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Ranking</p>
                    <p className="text-xl font-bold">{isLoadingPerformance ? '...' : `${performanceData?.data?.ranking || 0}º`}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Meta Diária */}
            <Card className="col-span-2">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <Target className="w-4 h-4 text-purple-500" />
                  <div className="w-full">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">Meta Diária</p>
                      <span className="text-sm font-semibold">{isLoadingPerformance ? '...' : `${performanceData?.data?.metasDiarias?.percentual || 0}%`}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-1 mt-2">
                      <div className="bg-purple-500 h-1 rounded-full" style={{ width: `${performanceData?.data?.metasDiarias?.percentual || 0}%` }} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Recuperado Hoje */}
            <Card className="col-span-2">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Recuperado Hoje</p>
                    <p className="text-xl font-bold">{isLoadingPerformance ? '...' : formatCurrency(performanceData?.data?.valorRecuperadoHoje || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Cards de Estatísticas Gerais */}
        <div>
          <h4 className="font-semibold text-sm text-foreground mb-3">📈 Estatísticas Gerais</h4>
          <div className="grid grid-cols-2 gap-3">
            {/* Card 1: Clientes e Títulos */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-blue-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Clientes</p>
                      <p className="text-xl font-bold">
                        {isLoadingStats ? '...' : (estatisticasData?.data?.estatisticas?.totalClientes || 0)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Building2 className="w-4 h-4 text-green-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Total Títulos</p>
                      <p className="text-xl font-bold">
                        {isLoadingStats ? '...' : (estatisticasData?.data?.estatisticas?.totalTitulos || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Valores e Pagamentos */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="w-4 h-4 text-purple-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Valor Total</p>
                      <p className="text-xl font-bold">
                        {isLoadingStats ? '...' : formatCurrency(estatisticasData?.data?.estatisticas?.valorTotalVencido || 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {isLoadingStats ? '...' : `${estatisticasData?.data?.estatisticas?.titulosVencidos || 0} títulos vencidos`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Pagamentos Hoje</p>
                      <p className="text-xl font-bold">
                        {isLoadingStats ? '...' : (estatisticasData?.data?.estatisticas?.clientesComPagamentoHoje || 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {isLoadingStats ? '...' : formatCurrency(estatisticasData?.data?.estatisticas?.valorPagamentosHoje || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
          </div>
        </div>
      )}
      {/* Modal de Transferência de Kanban */}
      {selectedCard && (
        <TituloTransferModal
          isOpen={isTransferKanbanModalOpen}
          onClose={() => {
            console.log('🔍 Fechando modal de transferência');
            setIsTransferKanbanModalOpen(false);
          }}
          titulo={{
            id: selectedCard.titulos?.[0]?.id || '',
            numero: selectedCard.titulos?.[0]?.numero || '',
            clienteNome: selectedCard.cliente?.nome || '',
            valor: selectedCard.titulos?.reduce((sum: number, t: any) => sum + Number(t.valor), 0) || 0,
            kanbanId: selectedKanbanId || '',
            kanbanNome: selectedKanban?.nome || '',
            etapaId: selectedCard.titulos?.[0]?.kanbanEtapaId || '',
            etapaNome: selectedCard.titulos?.[0]?.kanbanEtapa?.nome || '',
            tituloIds: (selectedCard.titulos || []).map((t: any) => t.id).filter(Boolean),
          }}
        />
      )}

      {/* Botão Flutuante WhatsApp */}
      {selectedCards.size > 0 && (
        <button
          onClick={handleOpenWhatsAppModal}
          className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2 group"
          title={`Enviar WhatsApp para ${selectedCards.size} cliente(s)`}
        >
          <MessageCircle className="h-6 w-6" />
          <span className="bg-card text-green-600 dark:text-green-400 rounded-full px-2 py-1 text-sm font-semibold border border-border">
            {selectedCards.size}
          </span>
        </button>
      )}

      {/* Modal WhatsApp em Massa */}
      {kanbanData?.data?.kanban && (
        <WhatsAppBulkModal
          isOpen={isWhatsAppModalOpen}
          onClose={() => {
            setIsWhatsAppModalOpen(false);
          }}
          cards={(Object.values(kanbanData.data.kanban) as KanbanCard[][])
            .flat()
            .filter((card) => selectedCards.has(card.id))}
          onSuccess={() => {
            setSelectedCards(new Set());
            setIsWhatsAppModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

// Componente Timeline para exibir histórico de status
interface TimelineCardProps {
  tituloId: string;
  tituloNumero: string;
}

const TimelineCard: React.FC<TimelineCardProps> = ({ tituloId, tituloNumero }) => {
  const { data: historico, isLoading } = useQuery({
    queryKey: ['kanban-history', tituloId],
    queryFn: async () => {
      const response = await api.get(`/titulos/${tituloId}/kanban-history`);
      return response.data.data.historico as Array<{
        id: string;
        kanbanAnteriorNome: string | null;
        kanbanNovoNome: string | null;
        etapaAnteriorNome: string | null;
        etapaNovoNome: string | null;
        origem: string | null;
        createdAt: string;
        motivo: string | null;
      }>;
    },
    enabled: !!tituloId,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!historico || historico.length === 0) {
    return (
      <Card>
        <CardContent className="p-4">
          <h4 className="font-semibold text-sm mb-2">Título: {tituloNumero}</h4>
          <p className="text-sm text-muted-foreground">Nenhum histórico encontrado</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <h4 className="font-semibold text-sm mb-4">Título: {tituloNumero}</h4>
        <div className="relative">
          {/* Linha vertical da timeline */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-slate-700"></div>
          
          {/* Itens da timeline */}
          <div className="space-y-4">
            {historico.map((item, index) => {
              const mudouKanban = item.kanbanAnteriorNome !== item.kanbanNovoNome;
              const mudouEtapa = item.etapaAnteriorNome !== item.etapaNovoNome;
              
              return (
                <div key={item.id} className="relative flex items-start space-x-4">
                  {/* Círculo da timeline */}
                  <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    index === 0 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-300 dark:bg-slate-600 text-muted-foreground'
                  }`}>
                    {index === 0 ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-current"></div>
                    )}
                  </div>
                  
                  {/* Conteúdo */}
                  <div className="flex-1 min-w-0 pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {mudouKanban ? (
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-foreground">
                              Kanban: {item.kanbanAnteriorNome || 'Sem kanban'} → {item.kanbanNovoNome || 'Sem kanban'}
                            </p>
                            {mudouEtapa && (
                              <p className="text-sm text-gray-600 dark:text-slate-400">
                                Etapa: {item.etapaAnteriorNome || 'Sem etapa'} → {item.etapaNovoNome || 'Sem etapa'}
                              </p>
                            )}
                          </div>
                        ) : mudouEtapa ? (
                          <p className="text-sm font-medium text-foreground">
                            Etapa: {item.etapaAnteriorNome || 'Sem etapa'} → {item.etapaNovoNome || 'Sem etapa'}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-600 dark:text-slate-400">Sem mudanças</p>
                        )}
                        
                        {item.motivo && (
                          <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                            Motivo: {item.motivo}
                          </p>
                        )}
                        
                        <div className="flex items-center space-x-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {item.origem || 'MANUAL'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(item.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
